import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "../../utils/axiosInstance";
import PageWrapper from "../../components/ui/PageWrapper";
import PageHeader from "../../components/ui/PageHeader";
import DateRangePicker from "../../components/ui/DateRangePicker";
import ProjectStatusSymbol from "../../components/project/ProjectStatusSymbol";
import LineItemAssignmentToolbar from "../../components/admin/LineItemAssignmentToolbar";
import InvoiceReviewModal from "../../components/admin/InvoiceReviewModal";
import { InvoiceConfidenceCell, InvoiceStatusBadge } from "../../components/admin/InvoiceTableCells";
import TaskBuilderModal from "../../components/ProjectTasks/TaskBuilderModal";
import { getTaskDisplayParts } from "../../components/ProjectEconomy/expectedCostUtils";
import {
  getAssignmentProgress,
  getInvoiceUnassignedCount,
  findNextInvoiceWithUnassigned,
  findFirstInvoiceWithUnassigned,
} from "../../utils/lineItemAssignmentUtils";
import { downloadInvoicePdf, viewInvoicePdf } from "../../utils/invoicePdfUtils";
import { normalizeProjectId } from "../../utils/invoiceDisplayUtils";
import { deleteInvoiceWithConfirm } from "../../utils/invoiceDeleteUtils";
import { computeFloatingMenuPosition } from "../../utils/floatingMenuPosition";
import { FaAlignLeft } from "react-icons/fa";
import dayjs from "dayjs";

const BILLING_OPTIONS = [
  { value: "", label: "All" },
  { value: "inherit", label: "Inherit" },
  { value: "fixed", label: "Fixed" },
  { value: "extra", label: "Extra" },
  { value: "included", label: "Included" },
  { value: "not_billable", label: "Not billable" },
  { value: "hide_from_project", label: "Hide from project" },
];

const NOT_BILLABLE_BADGE_CLASS =
  "inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-300/60";
const HIDE_FROM_PROJECT_BADGE_CLASS =
  "inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-900 ring-1 ring-inset ring-slate-400/70";

function billingDecisionLabel(value) {
  const v = String(value ?? "").trim();
  if (!v) return "—";
  const opt = BILLING_OPTIONS.find((o) => o.value === v);
  return opt?.label ?? v;
}

function isLineItemNotBillable(li) {
  return String(li?.billing ?? "").trim() === "not_billable";
}

function isLineItemHiddenFromProject(li) {
  return String(li?.billing ?? "").trim() === "hide_from_project";
}

function isLineItemExcludedFromBillableNet(li) {
  return isLineItemNotBillable(li) || isLineItemHiddenFromProject(li);
}

/** Skip PDF download when every line on the invoice is marked not billable. */
function isInvoiceEligibleForPdfDownload(invoiceId, invoices) {
  const inv = (invoices || []).find((i) => i._id != null && String(i._id) === String(invoiceId));
  if (!inv) return true;
  if (inv.hasNonNotBillableLine != null) return !!inv.hasNonNotBillableLine;
  return true;
}

function invoiceBillableNet(inv) {
  if (inv?.billableNet != null) return Number(inv.billableNet);
  return inv?.net != null ? Number(inv.net) : 0;
}

/** Positive line nets vs credit (negative) line nets for an invoice. */
function splitInvoiceNetAmount(net) {
  const n = net != null ? Number(net) : 0;
  if (!Number.isFinite(n) || n === 0) return { billable: 0, credits: 0 };
  if (n < 0) return { billable: 0, credits: n };
  return { billable: n, credits: 0 };
}

function formatCreditsNetCell(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n === 0) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatHoursReducedCell(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n === 0) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Line-break heading when label is >9 chars and has multiple words. */
function CustomerInvoiceTableHeading({ label, className = "" }) {
  const text = String(label ?? "").trim();
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1 || text.length <= 9) {
    return <span className={className}>{text}</span>;
  }
  return (
    <span className={className}>
      {words.map((word, i) => (
        <React.Fragment key={`${word}-${i}`}>
          {i > 0 && <br />}
          {word}
        </React.Fragment>
      ))}
    </span>
  );
}

/** Net × (1 + pct/100) using the % Addon field value (non-negative integer pct). */
function netWithAddonPct(net, addonPctStr) {
  const n = net != null ? Number(net) : 0;
  const raw = String(addonPctStr ?? "").trim();
  const pct = raw === "" ? 0 : Math.floor(Number(raw)) || 0;
  return pct ? n * (1 + pct / 100) : n;
}

const INVOICE_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In review" },
  { value: "reviewed", label: "Reviewed" },
  { value: "invoiced", label: "Invoiced" },
];

function customerInvoiceFilterLabel(selected) {
  if (!selected?.length) return "Our inv. #";
  if (selected.length === 1) return selected[0];
  return `${selected.length} inv. #`;
}

function customerInvoiceChipTitle(invoice) {
  const number = String(invoice?.invoicedNumber ?? "").trim();
  const date = invoice?.invoicedAt ? dayjs(invoice.invoicedAt).format("YYYY-MM-DD") : "";
  return [`Customer invoice ${number}`, date, "Double-click to copy"].filter(Boolean).join(" · ");
}

const REPORT_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In review" },
  { value: "reviewed", label: "Reviewed" },
  { value: "done", label: "Reviewed (legacy)" },
  { value: "invoiced", label: "Invoiced" },
  { value: "not_billable", label: "Not billable" },
  { value: "reduced", label: "Reduced" },
];
const UNBILLED_REPORT_STATUSES = ["new", "in_review", "reviewed", "done"];

const MANUAL_REPORT_STATUS_OPTIONS = [
  { value: "invoiced", label: "Invoiced" },
  { value: "new", label: "New" },
  { value: "not_billable", label: "Not billable" },
  { value: "reduced", label: "Reduced" },
];

const LABOUR_STATUS_LABELS = {
  new: "New",
  in_review: "In review",
  reviewed: "Reviewed",
  done: "Reviewed",
  invoiced: "Invoiced",
  not_billable: "Not billable",
  reduced: "Reduced",
};

function isReportLabourUnbilled(report) {
  return report?.status !== "invoiced" && report?.status !== "not_billable";
}

function calculateReducedReportAmount(report, rate) {
  const safeRate = Number(rate || 0);
  const hours = Number(report?.hours || 0);
  const baseTotal = hours * safeRate;
  if (!report || report.status === "not_billable") {
    return 0;
  }
  if (!report.reduction) {
    return baseTotal;
  }

  const reductionValue = Number(report.reduction?.value || 0);
  const reductionMode = report.reduction?.mode;
  if (!Number.isFinite(reductionValue) || reductionValue <= 0) {
    return baseTotal;
  }

  if (reductionMode === "percentage") {
    const multiplier = Math.max(0, 1 - reductionValue / 100);
    return baseTotal * multiplier;
  }
  if (reductionMode === "amount") {
    return Math.max(0, baseTotal - reductionValue);
  }
  if (reductionMode === "hours") {
    const billableHours = Math.max(0, hours - reductionValue);
    return billableHours * safeRate;
  }
  return baseTotal;
}

function formatReduction(report) {
  if (!report?.reduction) return "—";
  const value = Number(report.reduction.value || 0);
  if (report.reduction.mode === "percentage") return `${value}% lower hourly rate`;
  if (report.reduction.mode === "amount") return `${value.toFixed(2)} deducted`;
  if (report.reduction.mode === "hours") return `${value.toFixed(2)} hours deducted`;
  return "—";
}

function formatOverviewUnbilledRange(from, to) {
  if (!from || !to) return "—";
  const a = dayjs(from).format("YYYY-MM-DD");
  const b = dayjs(to).format("YYYY-MM-DD");
  return a === b ? a : `${a} – ${b}`;
}

const CopyIcon = ({ className = "h-4 w-4" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PRESETS = [
  {
    id: "last-30-days",
    label: "Last 30 days",
    getRange: () => ({
      from: dayjs().subtract(29, "day").startOf("day").toDate(),
      to: dayjs().endOf("day").toDate(),
    }),
  },
  {
    id: "this-month",
    label: "This month",
    getRange: () => ({
      from: dayjs().startOf("month").toDate(),
      to: dayjs().endOf("month").toDate(),
    }),
  },
  {
    id: "this-year",
    label: "This year",
    getRange: () => ({
      from: dayjs().startOf("year").toDate(),
      to: dayjs().endOf("year").toDate(),
    }),
  },
];

function getReportWrittenDescriptionDisplay(report) {
  const text = (report.descriptionDisplay ?? report.description ?? "").trim();
  return text || null;
}

function formatReportDescription(report) {
  if (report.taskEntries?.length > 0) {
    return report.taskEntries
      .map((e) => `${e.title || "Task"} ${e.hours}h`)
      .join(", ");
  }
  return report.description || "—";
}

/** Description in user's language (uses server-resolved *Display fields). */
function formatReportDescriptionDisplay(report) {
  if (report.taskEntries?.length > 0) {
    return report.taskEntries
      .map((e) => `${e.titleDisplay ?? e.title ?? "Task"} ${e.hours}h`)
      .join(", ");
  }
  return report.descriptionDisplay ?? report.description ?? "—";
}

export default function BillingPage() {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [dateRange, setDateRange] = useState(() => ({
    from: dayjs().subtract(29, "day").startOf("day").toDate(),
    to: dayjs().endOf("day").toDate(),
  }));

  const [reports, setReports] = useState([]);
  const [workerRates, setWorkerRates] = useState({});
  const [expandedWorkerIds, setExpandedWorkerIds] = useState(() => new Set());
  const [expandedInvoiceIds, setExpandedInvoiceIds] = useState(() => new Set());
  const [reportsLoading, setReportsLoading] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);

  const [viewMode, setViewMode] = useState("invoices");
  const [taskFilter, setTaskFilter] = useState("");
  const [billingFilter, setBillingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(["new", "in_review", "reviewed"]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [customerInvoiceFilter, setCustomerInvoiceFilter] = useState([]);
  const [customerInvoiceDropdownOpen, setCustomerInvoiceDropdownOpen] = useState(false);
  const [customerInvoiceQuery, setCustomerInvoiceQuery] = useState("");
  const customerInvoiceDropdownRef = useRef(null);
  const [reportsStatusFilter, setReportsStatusFilter] = useState(UNBILLED_REPORT_STATUSES);
  const [reportsStatusDropdownOpen, setReportsStatusDropdownOpen] = useState(false);
  const reportsStatusDropdownRef = useRef(null);
  const [timeReportsExportOpen, setTimeReportsExportOpen] = useState(false);
  const timeReportsExportRef = useRef(null);

  const [lineItemsSortBy, setLineItemsSortBy] = useState("invoiceDate");
  const [lineItemsSortOrder, setLineItemsSortOrder] = useState("asc");

  const [addonPct, setAddonPct] = useState("");
  const [applyAddonToInvoiceNetColumn, setApplyAddonToInvoiceNetColumn] = useState(false);
  const [netSum, setNetSum] = useState(0);
  const [highlightedLineItemIds, setHighlightedLineItemIds] = useState(() => new Set());
  const [highlightedInvoiceIds, setHighlightedInvoiceIds] = useState(() => new Set());
  const [copyPopover, setCopyPopover] = useState(null);
  const [downloadingPdfs, setDownloadingPdfs] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [markInvoicedLoading, setMarkInvoicedLoading] = useState(false);
  const [showMarkInvoicedModal, setShowMarkInvoicedModal] = useState(false);
  const [markInvoicedNumber, setMarkInvoicedNumber] = useState("");
  const [markInvoicedDate, setMarkInvoicedDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [billingOverview, setBillingOverview] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [billingGroups, setBillingGroups] = useState([]);
  const [billingGroupsLoading, setBillingGroupsLoading] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [markReportsLoading, setMarkReportsLoading] = useState(false);
  const [selectedManualStatus, setSelectedManualStatus] = useState("");
  const [showReducedModal, setShowReducedModal] = useState(false);
  const [reducedMode, setReducedMode] = useState("percentage");
  const [reducedValue, setReducedValue] = useState("");
  const [reducedNote, setReducedNote] = useState("");
  const [showLabourInvoicedModal, setShowLabourInvoicedModal] = useState(false);
  const [labourInvoicedNumber, setLabourInvoicedNumber] = useState("");
  const [labourInvoicedDate, setLabourInvoicedDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [expandedBillingGroupKeys, setExpandedBillingGroupKeys] = useState(() => new Set());
  const projectSelectRef = useRef(null);
  const netSumInputRef = React.useRef(null);
  const copyPopoverTimeoutRef = React.useRef(null);

  const [taskBundle, setTaskBundle] = useState(null);
  const [selectedLineItemIdsByInvoice, setSelectedLineItemIdsByInvoice] = useState({});
  const [lineItemTaskOverride, setLineItemTaskOverride] = useState("");
  const [lineItemBillingOption, setLineItemBillingOption] = useState("included");
  const [lineItemApplySaving, setLineItemApplySaving] = useState(false);
  const [needsTaskFilter, setNeedsTaskFilter] = useState(false);
  const [reviewInvoice, setReviewInvoice] = useState(null);
  const [reviewLineItemIds, setReviewLineItemIds] = useState([]);
  const [saveInvoiceLoading, setSaveInvoiceLoading] = useState(false);
  const [deleteInvoiceLoading, setDeleteInvoiceLoading] = useState(false);
  const [saveInvoiceError, setSaveInvoiceError] = useState(null);
  const [openActionsId, setOpenActionsId] = useState(null);
  const [actionsDropdownPosition, setActionsDropdownPosition] = useState(null);
  const actionsDropdownRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [newTaskModalProjectId, setNewTaskModalProjectId] = useState("");
  const ACTIONS_MENU_WIDTH = 160;
  const REPORT_DESCRIPTION_POPOVER_WIDTH = 320;
  const [reportDescriptionPopover, setReportDescriptionPopover] = useState(null);
  const reportDescriptionPopoverRef = useRef(null);

  const hasProjectAndRange = selectedProjectId && dateRange?.from && dateRange?.to;
  const startDate = dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : "";
  const endDate = dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : "";

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    axios
      .get("/projects", { params: { limit: 500 } })
      .then((res) => {
        if (!cancelled) setProjects(res.data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchBillingOverview = useCallback(() => {
    setOverviewLoading(true);
    axios
      .get("/admin/billing/overview")
      .then((res) => {
        setBillingOverview(res.data?.items ?? []);
      })
      .catch(() => setBillingOverview([]))
      .finally(() => setOverviewLoading(false));
  }, []);

  useEffect(() => {
    fetchBillingOverview();
  }, [fetchBillingOverview]);

  const fetchProjectBillingGroups = useCallback(() => {
    if (!selectedProjectId) {
      setBillingGroups([]);
      return;
    }
    setBillingGroupsLoading(true);
    axios
      .get(`/admin/billing/projects/${selectedProjectId}/invoices`)
      .then((res) => {
        setBillingGroups(res.data?.groups ?? []);
      })
      .catch(() => setBillingGroups([]))
      .finally(() => setBillingGroupsLoading(false));
  }, [selectedProjectId]);

  useEffect(() => {
    fetchProjectBillingGroups();
  }, [fetchProjectBillingGroups]);

  useEffect(() => {
    setSelectedReportIds([]);
  }, [selectedProjectId, startDate, endDate]);

  const fetchReports = useCallback(() => {
    if (!selectedProjectId || !startDate || !endDate) return;
    setReportsLoading(true);
    axios
      .get(`/projects/${selectedProjectId}/time-reports`, {
        params: { startDate, endDate, limit: 2000 },
      })
      .then((res) => {
        setReports(res.data?.reports ?? []);
        setWorkerRates(res.data?.workerRates ?? {});
      })
      .catch(() => {
        setReports([]);
        setWorkerRates({});
      })
      .finally(() => setReportsLoading(false));
  }, [selectedProjectId, startDate, endDate]);

  const fetchLineItems = useCallback(() => {
    if (!selectedProjectId || !startDate || !endDate) return;
    setLineItemsLoading(true);
    const params = { projectId: selectedProjectId, startDate, endDate };
    if (taskFilter.trim()) params.task = taskFilter.trim();
    if (billingFilter.trim()) params.billing = billingFilter.trim();
    const statuses = Array.isArray(statusFilter) ? [...statusFilter] : [];
    // Customer invoice numbers only exist on invoiced rows. Include that status
    // for this request so the number filter can match without changing the checkboxes.
    if (
      customerInvoiceFilter.length > 0 &&
      statuses.length > 0 &&
      !statuses.includes("invoiced")
    ) {
      statuses.push("invoiced");
    }
    if (statuses.length > 0) {
      params.status = statuses.join(",");
    }
    if (customerInvoiceFilter.length > 0) {
      params.invoicedNumber = customerInvoiceFilter.join(",");
    }
    axios
      .get("/admin/invoices/line-items", { params })
      .then((res) => {
        setLineItems(res.data?.lineItems ?? []);
        setTaskOptions(res.data?.taskOptions ?? []);
        setInvoices(res.data?.invoices ?? []);
      })
      .catch(() => {
        setLineItems([]);
        setTaskOptions([]);
        setInvoices([]);
      })
      .finally(() => setLineItemsLoading(false));
  }, [selectedProjectId, startDate, endDate, taskFilter, billingFilter, statusFilter, customerInvoiceFilter]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const onMouseDown = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [statusDropdownOpen]);

  useEffect(() => {
    setCustomerInvoiceFilter((current) => (current.length === 0 ? current : []));
    setCustomerInvoiceDropdownOpen(false);
    setCustomerInvoiceQuery("");
  }, [selectedProjectId]);

  useEffect(() => {
    if (!customerInvoiceDropdownOpen) return;
    const onMouseDown = (e) => {
      if (customerInvoiceDropdownRef.current && !customerInvoiceDropdownRef.current.contains(e.target)) {
        setCustomerInvoiceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [customerInvoiceDropdownOpen]);

  useEffect(() => {
    if (!timeReportsExportOpen) return;
    const onMouseDown = (e) => {
      if (timeReportsExportRef.current && !timeReportsExportRef.current.contains(e.target)) {
        setTimeReportsExportOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [timeReportsExportOpen]);

  useEffect(() => {
    if (!reportsStatusDropdownOpen) return;
    const onMouseDown = (e) => {
      if (reportsStatusDropdownRef.current && !reportsStatusDropdownRef.current.contains(e.target)) {
        setReportsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [reportsStatusDropdownOpen]);

  useEffect(() => {
    if (!hasProjectAndRange) return;
    fetchReports();
  }, [hasProjectAndRange, fetchReports]);

  useEffect(() => {
    if (!hasProjectAndRange) return;
    fetchLineItems();
  }, [hasProjectAndRange, fetchLineItems]);

  useEffect(() => {
    if (!selectedProjectId) {
      setTaskBundle(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/task-bundles/projects/${selectedProjectId}/task-bundle`)
      .then((res) => {
        if (!cancelled) setTaskBundle(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.response?.status === 404) setTaskBundle(null);
          else setTaskBundle(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!openActionsId) return undefined;
    const onMouseDown = (e) => {
      if (
        actionsDropdownRef.current &&
        !actionsDropdownRef.current.contains(e.target) &&
        !actionsMenuRef.current?.contains(e.target)
      ) {
        setOpenActionsId(null);
        setActionsDropdownPosition(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [openActionsId]);

  const updateInvoiceActionsMenuPosition = useCallback(() => {
    if (!openActionsId) return;
    const trigger = document.querySelector(
      `[data-invoice-actions-trigger="${openActionsId}"]`
    );
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = actionsMenuRef.current?.getBoundingClientRect().height;
    setActionsDropdownPosition(
      computeFloatingMenuPosition({
        triggerRect: rect,
        menuHeight: menuHeight || 120,
        menuWidth: ACTIONS_MENU_WIDTH,
        align: "end",
      })
    );
  }, [openActionsId]);

  useEffect(() => {
    if (!openActionsId) return undefined;
    updateInvoiceActionsMenuPosition();
    window.addEventListener("resize", updateInvoiceActionsMenuPosition);
    window.addEventListener("scroll", updateInvoiceActionsMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateInvoiceActionsMenuPosition);
      window.removeEventListener("scroll", updateInvoiceActionsMenuPosition, true);
    };
  }, [openActionsId, updateInvoiceActionsMenuPosition]);

  useEffect(() => {
    if (!openActionsId || !actionsMenuRef.current) return;
    updateInvoiceActionsMenuPosition();
  }, [openActionsId, actionsDropdownPosition?.top, updateInvoiceActionsMenuPosition]);

  useEffect(() => {
    if (!reportDescriptionPopover) return undefined;
    const onMouseDown = (e) => {
      if (
        reportDescriptionPopoverRef.current?.contains(e.target) ||
        e.target.closest(`[data-report-description-trigger="${reportDescriptionPopover.reportId}"]`)
      ) {
        return;
      }
      setReportDescriptionPopover(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [reportDescriptionPopover]);

  const updateReportDescriptionPopoverPosition = useCallback(() => {
    if (!reportDescriptionPopover?.reportId) return;
    const trigger = document.querySelector(
      `[data-report-description-trigger="${reportDescriptionPopover.reportId}"]`
    );
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = reportDescriptionPopoverRef.current?.getBoundingClientRect().height;
    setReportDescriptionPopover((prev) => {
      if (!prev) return prev;
      const position = computeFloatingMenuPosition({
        triggerRect: rect,
        menuHeight: menuHeight || 100,
        menuWidth: REPORT_DESCRIPTION_POPOVER_WIDTH,
        align: "start",
      });
      return { ...prev, ...position };
    });
  }, [reportDescriptionPopover?.reportId]);

  useEffect(() => {
    if (!reportDescriptionPopover) return undefined;
    updateReportDescriptionPopoverPosition();
    window.addEventListener("resize", updateReportDescriptionPopoverPosition);
    window.addEventListener("scroll", updateReportDescriptionPopoverPosition, true);
    return () => {
      window.removeEventListener("resize", updateReportDescriptionPopoverPosition);
      window.removeEventListener("scroll", updateReportDescriptionPopoverPosition, true);
    };
  }, [reportDescriptionPopover, updateReportDescriptionPopoverPosition]);

  useEffect(() => {
    if (!reportDescriptionPopover || !reportDescriptionPopoverRef.current) return;
    updateReportDescriptionPopoverPosition();
  }, [reportDescriptionPopover, updateReportDescriptionPopoverPosition]);

  useEffect(() => {
    setSelectedInvoiceIds((prev) =>
      prev.filter((id) => invoices.some((inv) => String(inv._id) === id))
    );
  }, [invoices]);

  const filteredReports = React.useMemo(() => {
    const statuses = Array.isArray(reportsStatusFilter) ? reportsStatusFilter : [];
    if (statuses.length === 0) return reports || [];
    const allowed = new Set(statuses);
    return (reports || []).filter((r) => allowed.has(r?.status ?? "new"));
  }, [reports, reportsStatusFilter]);

  const groupedByWorker = React.useMemo(() => {
    if (!filteredReports.length) return [];
    const byId = new Map();
    for (const r of filteredReports) {
      const workerId = r.worker?._id?.toString() ?? "unknown";
      if (!byId.has(workerId)) {
        byId.set(workerId, {
          workerId,
          workerName: r.worker?.name ?? "Unknown",
          totalHours: 0,
          rate: workerRates[workerId] ?? 0,
          totalAmount: 0,
          reports: [],
        });
      }
      const row = byId.get(workerId);
      const reportHours = r.hours != null ? Number(r.hours) : 0;
      const workerRate = workerRates[workerId] ?? row.rate ?? 0;
      row.totalHours += reportHours;
      row.totalAmount += calculateReducedReportAmount(r, workerRate);
      row.reports.push(r);
    }
    return [...byId.values()].sort((a, b) => a.workerName.localeCompare(b.workerName));
  }, [filteredReports, workerRates]);

  const sortedLineItems = React.useMemo(() => {
    if (!lineItems.length) return [];
    const key = lineItemsSortBy;
    const order = lineItemsSortOrder === "asc" ? 1 : -1;
    return [...lineItems].sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === "invoiceDate") {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return order * (va - vb);
      }
      if (key === "net") {
        va = Number(va);
        vb = Number(vb);
        return order * (va - vb);
      }
      const strA = String(va ?? "").toLowerCase();
      const strB = String(vb ?? "").toLowerCase();
      return order * strA.localeCompare(strB);
    });
  }, [lineItems, lineItemsSortBy, lineItemsSortOrder]);

  const billingDecisionsByInvoiceId = React.useMemo(() => {
    const map = new Map();
    (lineItems || []).forEach((li) => {
      const id = li.invoiceId != null ? String(li.invoiceId) : null;
      if (!id) return;
      if (!map.has(id)) map.set(id, { decisions: new Set(), hasMissing: false });
      const entry = map.get(id);
      const b = li.billing;
      if (b == null || String(b).trim() === "") {
        entry.hasMissing = true;
      } else {
        entry.decisions.add(String(b).trim());
      }
    });
    return map;
  }, [lineItems]);

  const invoiceBillableSplitByInvoiceId = React.useMemo(() => {
    const map = new Map();
    (lineItems || []).forEach((li) => {
      const id = li.invoiceId != null ? String(li.invoiceId) : null;
      if (!id) return;
      const liNet = li?.net != null ? Number(li.net) : 0;
      if (!Number.isFinite(liNet) || isLineItemExcludedFromBillableNet(li)) return;
      const entry = map.get(id) || { billable: 0, credits: 0 };
      if (liNet > 0) entry.billable += liNet;
      else if (liNet < 0) entry.credits += liNet;
      map.set(id, entry);
    });
    return map;
  }, [lineItems]);

  const getInvoiceBillableAndCredits = useCallback(
    (inv) => {
      const id = inv?._id != null ? String(inv._id) : null;
      if (id && invoiceBillableSplitByInvoiceId.has(id)) {
        return invoiceBillableSplitByInvoiceId.get(id);
      }
      return splitInvoiceNetAmount(invoiceBillableNet(inv));
    },
    [invoiceBillableSplitByInvoiceId]
  );

  const getInvoiceEffectiveBillableNet = useCallback(
    (inv) => {
      const { billable, credits } = getInvoiceBillableAndCredits(inv);
      return billable + credits;
    },
    [getInvoiceBillableAndCredits]
  );

  const sortedInvoices = React.useMemo(() => {
    if (!invoices.length) return [];
    const key = lineItemsSortBy;
    const order = lineItemsSortOrder === "asc" ? 1 : -1;
    return [...invoices].sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === "invoiceDate") {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return order * (va - vb);
      }
      if (key === "net") {
        const baseA = getInvoiceEffectiveBillableNet(a);
        const baseB = getInvoiceEffectiveBillableNet(b);
        va = applyAddonToInvoiceNetColumn ? netWithAddonPct(baseA, addonPct) : baseA;
        vb = applyAddonToInvoiceNetColumn ? netWithAddonPct(baseB, addonPct) : baseB;
        return order * (va - vb);
      }
      const strA = String(va ?? "").toLowerCase();
      const strB = String(vb ?? "").toLowerCase();
      return order * strA.localeCompare(strB);
    });
  }, [invoices, lineItemsSortBy, lineItemsSortOrder, applyAddonToInvoiceNetColumn, addonPct, getInvoiceEffectiveBillableNet]);

  const displayInvoices = React.useMemo(() => {
    if (!needsTaskFilter) return sortedInvoices;
    return sortedInvoices.filter((inv) => getInvoiceUnassignedCount(inv._id, lineItems) > 0);
  }, [sortedInvoices, needsTaskFilter, lineItems]);

  const customerInvoiceOptions = React.useMemo(() => {
    const numbers = new Set();
    for (const group of billingGroups || []) {
      const number = String(group?.invoicedNumber ?? "").trim();
      if (number) numbers.add(number);
    }
    for (const inv of invoices || []) {
      const number = String(inv?.invoicedNumber ?? "").trim();
      if (number) numbers.add(number);
    }
    return [...numbers].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
  }, [billingGroups, invoices]);

  const visibleCustomerInvoiceOptions = React.useMemo(() => {
    const query = customerInvoiceQuery.trim().toLowerCase();
    if (!query) return customerInvoiceOptions;
    return customerInvoiceOptions.filter((number) => number.toLowerCase().includes(query));
  }, [customerInvoiceOptions, customerInvoiceQuery]);

  const assignmentProgress = React.useMemo(
    () => getAssignmentProgress(lineItems),
    [lineItems]
  );

  useEffect(() => {
    if (!needsTaskFilter || viewMode !== "invoices") return;
    const first = findFirstInvoiceWithUnassigned(displayInvoices, lineItems);
    if (first?._id) {
      setExpandedInvoiceIds((prev) => {
        const next = new Set(prev);
        next.add(String(first._id));
        return next;
      });
    }
  }, [needsTaskFilter, viewMode, displayInvoices, lineItems]);

  const handleSort = (column) => {
    if (lineItemsSortBy === column) {
      setLineItemsSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setLineItemsSortBy(column);
      setLineItemsSortOrder("asc");
    }
  };

  const getLineItemKey = (li) => li.lineItemId?.toString() ?? `${li.invoiceId}-${li.name}`;

  const getNetWithAddon = (net) => netWithAddonPct(net, addonPct);

  const handleNetCellClick = (li) => {
    if (isLineItemExcludedFromBillableNet(li)) return;
    const net = li.net != null ? Number(li.net) : 0;
    if (net === 0) return;
    const amount = getNetWithAddon(net);
    setNetSum((s) => s + amount);
    setHighlightedLineItemIds((prev) => new Set(prev).add(getLineItemKey(li)));
  };

  const handleLineItemNetClick = (li, e) => {
    if (isLineItemExcludedFromBillableNet(li)) return;
    handleNetCellClick(li);
    const net = li.net != null ? Number(li.net) : 0;
    if (net !== 0) {
      handleCopyToClipboard(getNetWithAddon(net).toFixed(2), e?.currentTarget ?? e);
    }
  };

  const handleInvoiceNetCellClick = (inv) => {
    const net = getInvoiceEffectiveBillableNet(inv);
    if (net === 0) return;
    const amount = getNetWithAddon(net);
    setNetSum((s) => s + amount);
    setHighlightedInvoiceIds((prev) => new Set(prev).add(inv._id.toString()));
  };

  const handleInvoiceNetClick = (inv, e) => {
    handleInvoiceNetCellClick(inv);
    const net = getInvoiceEffectiveBillableNet(inv);
    if (net !== 0) {
      handleCopyToClipboard(getNetWithAddon(net).toFixed(2), e?.currentTarget ?? e);
    }
  };

  const handleClearNetSum = () => {
    setNetSum(0);
    setHighlightedLineItemIds(new Set());
    setHighlightedInvoiceIds(new Set());
  };

  const showCopyPopover = (el) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2 - 20;
    if (copyPopoverTimeoutRef.current) clearTimeout(copyPopoverTimeoutRef.current);
    setCopyPopover({ x, y });
    copyPopoverTimeoutRef.current = setTimeout(() => {
      setCopyPopover(null);
      copyPopoverTimeoutRef.current = null;
    }, 1500);
  };

  const handleCopyToClipboard = async (text, eventOrEl) => {
    const value = String(text ?? "").trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      const el = eventOrEl?.currentTarget ?? eventOrEl?.target ?? eventOrEl;
      if (el) showCopyPopover(el);
    } catch {
      // ignore
    }
  };

  const materialLabelText = (supplier, invoiceNumber) =>
    `Material - ${supplier ?? ""} (${invoiceNumber ?? ""})`;

  const mergeLineItemsFromInvoice = useCallback((updatedInvoice) => {
    if (!updatedInvoice?._id) return;
    const invId = String(updatedInvoice._id);
    const bundle = taskBundle;
    const taskOverrideToName = new Map();
    if (bundle?.tasks) {
      for (const t of bundle.tasks) {
        taskOverrideToName.set(`task:${t._id}`, t.title ?? "Task");
        for (const s of t.subtasks || []) {
          taskOverrideToName.set(
            `subtask:${t._id}:${s._id}`,
            `${t.title ?? "Task"} / ${s.title ?? "Subtask"}`
          );
        }
      }
    }
    setLineItems((prev) => {
      const without = prev.filter((li) => String(li.invoiceId) !== invId);
      const fresh = (updatedInvoice.lineItems || []).map((li) => {
        const taskOverride = li.taskOverride ?? "";
        return {
          lineItemId: li._id,
          invoiceId: updatedInvoice._id,
          invoiceDate: updatedInvoice.invoiceDate,
          invoiceNumber: updatedInvoice.invoiceNumber ?? "",
          supplierName: updatedInvoice.supplierName ?? "",
          name: li.name ?? "",
          billing: li.billingDecision ?? "inherit",
          task: taskOverride,
          taskName: taskOverride ? (taskOverrideToName.get(taskOverride) ?? taskOverride) : "",
          net: li.net ?? 0,
        };
      });
      return [...without, ...fresh];
    });
    setInvoices((prev) =>
      prev.map((i) => {
        if (String(i._id) !== invId) return i;
        let visibleLineSum = 0;
        for (const li of updatedInvoice.lineItems || []) {
          const bd = String(li.billingDecision ?? "").trim();
          if (bd !== "hide_from_project") visibleLineSum += li.net != null ? Number(li.net) : 0;
        }
        const totalNet = updatedInvoice.totals?.net;
        const hasPriceDiff =
          totalNet != null && Math.abs(Number(totalNet) - visibleLineSum) > 0.01;
        return {
          ...i,
          status: updatedInvoice.status ?? i.status,
          confidence: updatedInvoice.confidence ?? i.confidence,
          needsReview: updatedInvoice.needsReview ?? i.needsReview,
          supplierName: updatedInvoice.supplierName ?? i.supplierName,
          invoiceNumber: updatedInvoice.invoiceNumber ?? i.invoiceNumber,
          invoiceDate: updatedInvoice.invoiceDate ?? i.invoiceDate,
          net: updatedInvoice.totals?.net ?? i.net,
          hasPriceDiff,
        };
      })
    );
  }, [taskBundle]);

  const toggleExpandedLineItemSelection = (invoiceId, lineItemId) => {
    setSelectedLineItemIdsByInvoice((prev) => {
      const list = prev[invoiceId] || [];
      const set = new Set(list);
      if (set.has(lineItemId)) set.delete(lineItemId);
      else set.add(lineItemId);
      const next = { ...prev, [invoiceId]: [...set] };
      if (next[invoiceId].length === 0) delete next[invoiceId];
      return next;
    });
  };

  const toggleAllExpandedLineItems = (invoiceId, invLineItems) => {
    const items = invLineItems || [];
    if (!items.length) return;
    const allIds = items.map((li) => li.lineItemId).filter(Boolean);
    const current = selectedLineItemIdsByInvoice[invoiceId] || [];
    const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
    setSelectedLineItemIdsByInvoice((prev) => {
      const next = { ...prev };
      if (allSelected) delete next[invoiceId];
      else next[invoiceId] = [...allIds];
      return next;
    });
  };

  const clearExpandedLineItemSelection = (invoiceId) => {
    setSelectedLineItemIdsByInvoice((prev) => {
      const next = { ...prev };
      delete next[invoiceId];
      return next;
    });
  };

  const handleApplyLineItemTaskAndBilling = async (invoiceId, selectedIds, options = {}) => {
    const { autoAdvance = true, invoiceLineItems } = options;
    const existingIds = (selectedIds || []).filter(
      (id) => typeof id === "string" && !id.startsWith("temp-")
    );
    if (!existingIds.length) return;
    setLineItemApplySaving(true);
    try {
      let updatedInvoice = null;
      for (const lineItemId of existingIds) {
        const res = await axios.patch(
          `/admin/invoices/${invoiceId}/line-items/${lineItemId}`,
          {
            taskOverride: lineItemTaskOverride ?? "",
            billingDecision: lineItemBillingOption,
          }
        );
        updatedInvoice = res.data.invoice;
      }
      if (updatedInvoice) {
        const invId = String(invoiceId);
        const bundle = taskBundle;
        const projectedLineItems = (() => {
          const taskOverrideToName = new Map();
          if (bundle?.tasks) {
            for (const t of bundle.tasks) {
              taskOverrideToName.set(`task:${t._id}`, t.title ?? "Task");
              for (const s of t.subtasks || []) {
                taskOverrideToName.set(
                  `subtask:${t._id}:${s._id}`,
                  `${t.title ?? "Task"} / ${s.title ?? "Subtask"}`
                );
              }
            }
          }
          const without = lineItems.filter((li) => String(li.invoiceId) !== invId);
          const fresh = (updatedInvoice.lineItems || []).map((li) => {
            const taskOverride = li.taskOverride ?? "";
            return {
              lineItemId: li._id,
              invoiceId: updatedInvoice._id,
              task: taskOverride,
              taskName: taskOverride ? (taskOverrideToName.get(taskOverride) ?? taskOverride) : "",
            };
          });
          return [...without, ...fresh];
        })();
        mergeLineItemsFromInvoice(updatedInvoice);
        if (reviewInvoice?._id === invoiceId) {
          setReviewInvoice(updatedInvoice);
        }
        clearExpandedLineItemSelection(invoiceId);
        setReviewLineItemIds([]);
        const invItems = invoiceLineItems || lineItems.filter(
          (li) => String(li.invoiceId) === invId
        );
        const appliedToAll =
          invItems.length > 0 && existingIds.length === invItems.length;
        const remainingUnassigned = getInvoiceUnassignedCount(invoiceId, projectedLineItems);
        if (autoAdvance && appliedToAll && remainingUnassigned === 0) {
          const list = needsTaskFilter ? displayInvoices : sortedInvoices;
          const nextInvoice = findNextInvoiceWithUnassigned(list, projectedLineItems, invoiceId);
          setExpandedInvoiceIds(() => {
            const next = new Set();
            if (nextInvoice?._id) next.add(String(nextInvoice._id));
            return next;
          });
        }
      }
    } catch (err) {
      console.error("Apply line item task/billing", err);
    } finally {
      setLineItemApplySaving(false);
    }
  };

  const openInvoiceReview = async (inv) => {
    if (!inv?._id) return;
    try {
      const res = await axios.get(`/admin/invoices/${inv._id}`);
      const data = res.data;
      setReviewInvoice({
        ...data,
        projectId: normalizeProjectId(data.projectId) || undefined,
      });
      setReviewLineItemIds([]);
      setSaveInvoiceError(null);
      setOpenActionsId(null);
      setActionsDropdownPosition(null);
    } catch (err) {
      console.error("Load invoice for review", err);
      alert(err.response?.data?.message || "Could not load invoice");
    }
  };

  const handleSaveReviewInvoice = async () => {
    if (!reviewInvoice?._id) return;
    setSaveInvoiceError(null);
    setSaveInvoiceLoading(true);
    try {
      const invoiceDate = reviewInvoice.invoiceDate
        ? (reviewInvoice.invoiceDate instanceof Date
            ? reviewInvoice.invoiceDate
            : new Date(reviewInvoice.invoiceDate)
          ).toISOString().slice(0, 10)
        : undefined;
      const payload = {
        projectId: normalizeProjectId(reviewInvoice.projectId) || undefined,
        projectCode: reviewInvoice.projectCode,
        billingDecision: reviewInvoice.billingDecision || undefined,
        notes: reviewInvoice.notes,
        status: reviewInvoice.status,
        supplierName: reviewInvoice.supplierName ?? undefined,
        invoiceNumber: reviewInvoice.invoiceNumber ?? undefined,
        invoiceDate: invoiceDate || undefined,
        totals: {
          ...(reviewInvoice.totals || {}),
          ...(reviewInvoice.totals?.net !== undefined &&
            !Number.isNaN(Number(reviewInvoice.totals.net)) && {
              net: Number(reviewInvoice.totals.net),
            }),
          ...(reviewInvoice.totals?.gross !== undefined &&
            !Number.isNaN(Number(reviewInvoice.totals.gross)) && {
              gross: Number(reviewInvoice.totals.gross),
            }),
        },
      };
      const lineItemsPayload = (reviewInvoice.lineItems || []).map((li) => {
        const id = li._id != null ? String(li._id) : null;
        const isNew = id && id.startsWith("temp-");
        const num = (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v) : undefined);
        const item = {
          articleNumber: li.articleNumber ?? undefined,
          name: li.name ?? "",
          amount: num(li.amount),
          unit: li.unit ?? undefined,
          net: num(li.net),
          gross: num(li.gross),
          billingDecision: li.billingDecision ?? "inherit",
          taskOverride: li.taskOverride ?? undefined,
          projectOverride: li.projectOverride ?? undefined,
          notes: li.notes ?? undefined,
        };
        if (!isNew && id) item._id = li._id;
        return item;
      });
      payload.lineItems = lineItemsPayload;
      const res = await axios.patch(`/admin/invoices/${reviewInvoice._id}`, payload);
      mergeLineItemsFromInvoice(res.data);
      setReviewInvoice(null);
      setReviewLineItemIds([]);
      fetchLineItems();
    } catch (err) {
      console.error("Save invoice", err);
      setSaveInvoiceError(err.response?.data?.message || "Failed to save invoice");
    } finally {
      setSaveInvoiceLoading(false);
    }
  };

  const handleDeleteReviewInvoice = async (invoice) => {
    setDeleteInvoiceLoading(true);
    try {
      const deleted = await deleteInvoiceWithConfirm(invoice);
      if (!deleted) return;
      setReviewInvoice(null);
      setReviewLineItemIds([]);
      setSaveInvoiceError(null);
      setOpenActionsId(null);
      setActionsDropdownPosition(null);
      setExpandedInvoiceIds((prev) => {
        const next = new Set(prev);
        next.delete(String(invoice._id));
        return next;
      });
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== String(invoice._id)));
      fetchLineItems();
      fetchBillingOverview();
      fetchProjectBillingGroups();
    } catch (err) {
      console.error("Delete invoice", err);
      alert(err.response?.data?.message || "Could not delete invoice");
    } finally {
      setDeleteInvoiceLoading(false);
    }
  };

  const handleViewPdf = async (invoiceId) => {
    try {
      await viewInvoicePdf(invoiceId);
      setOpenActionsId(null);
      setActionsDropdownPosition(null);
    } catch (err) {
      console.error("View PDF", err);
      alert(err.response?.data?.message || "Could not load PDF");
    }
  };

  const handleDownloadPdf = async (inv) => {
    try {
      await downloadInvoicePdf(inv, inv._id);
      setOpenActionsId(null);
      setActionsDropdownPosition(null);
    } catch (err) {
      console.error("Download PDF", err);
      alert(err.response?.data?.message || "Could not download PDF");
    }
  };

  const openInvoiceActionsMenu = (inv, e) => {
    if (openActionsId === inv._id) {
      setOpenActionsId(null);
      setActionsDropdownPosition(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setActionsDropdownPosition(
      computeFloatingMenuPosition({
        triggerRect: rect,
        menuWidth: ACTIONS_MENU_WIDTH,
        align: "end",
      })
    );
    setOpenActionsId(inv._id);
  };

  const toggleReportDescriptionPopover = (reportId, text, e) => {
    e.stopPropagation();
    if (reportDescriptionPopover?.reportId === reportId) {
      setReportDescriptionPopover(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const position = computeFloatingMenuPosition({
      triggerRect: rect,
      menuHeight: 100,
      menuWidth: REPORT_DESCRIPTION_POPOVER_WIDTH,
      align: "start",
    });
    setReportDescriptionPopover({ reportId, text, ...position });
  };

  const handleCreateTaskSuccess = (payload) => {
    const projectId = normalizeProjectId(
      newTaskModalProjectId ||
        selectedProjectId ||
        reviewInvoice?.projectId
    );

    if (payload?.bundle) {
      setTaskBundle(payload.bundle);
    } else if (projectId) {
      axios
        .get(`/task-bundles/projects/${projectId}/task-bundle`)
        .then((res) => setTaskBundle(res.data))
        .catch(() => {});
    }

    setShowCreateTaskModal(false);
    setNewTaskModalProjectId("");
  };

  const renderAssignedTaskCell = (li) => {
    const taskOverride = li.task ?? "";
    if (!taskOverride) {
      return <span className="text-amber-700 text-xs">Select task…</span>;
    }
    const parts = getTaskDisplayParts(taskOverride, taskBundle);
    if (parts.isSubtask) {
      return (
        <span className="block min-w-0">
          <span className="block text-xs text-gray-500 truncate">{parts.parent}</span>
          <span className="block text-sm text-gray-800 truncate">{parts.child}</span>
        </span>
      );
    }
    return <span className="text-sm text-gray-800 truncate">{parts.parent}</span>;
  };

  const toggleBillingInvoiceSelection = (id) => {
    const idStr = String(id);
    setSelectedInvoiceIds((prev) =>
      prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]
    );
  };

  const toggleAllBillingInvoices = () => {
    const ids = displayInvoices.map((inv) => String(inv._id)).filter(Boolean);
    const allSelected = ids.length > 0 && ids.every((id) => selectedInvoiceIds.includes(id));
    setSelectedInvoiceIds(allSelected ? [] : ids);
  };

  const handleMarkInvoicedOpen = () => {
    if (selectedInvoiceIds.length === 0) return;
    setShowMarkInvoicedModal(true);
  };

  const handleMarkInvoicedSubmit = async () => {
    const number = (markInvoicedNumber || "").trim();
    if (!number || !markInvoicedDate) return;
    const pctSnapshot = applyAddonToInvoiceNetColumn
      ? Math.floor(Number(addonPct)) || 0
      : 0;
    const invoicesById = new Map(
      (invoices || []).map((inv) => [String(inv._id), inv])
    );
    const amounts = selectedInvoiceIds.map((idStr) => {
      const inv = invoicesById.get(String(idStr));
      const { billable, credits } = inv ? getInvoiceBillableAndCredits(inv) : { billable: 0, credits: 0 };
      const invoicedBillableNet = applyAddonToInvoiceNetColumn
        ? netWithAddonPct(billable, addonPct)
        : billable;
      return {
        id: idStr,
        invoicedBillableNet,
        invoicedCreditsNet: credits,
        invoicedAddonPct: pctSnapshot,
      };
    });
    setMarkInvoicedLoading(true);
    try {
      await axios.post("/admin/invoices/mark-invoiced", {
        ids: selectedInvoiceIds,
        invoicedNumber: number,
        invoicedAt: new Date(markInvoicedDate).toISOString(),
        amounts,
      });
      setShowMarkInvoicedModal(false);
      setMarkInvoicedNumber("");
      setMarkInvoicedDate(dayjs().format("YYYY-MM-DD"));
      setSelectedInvoiceIds([]);
      fetchLineItems();
      fetchBillingOverview();
      fetchProjectBillingGroups();
    } catch (err) {
      console.error("Mark as invoiced", err);
    } finally {
      setMarkInvoicedLoading(false);
    }
  };

  const toggleReportSelection = (id) => {
    const idStr = String(id);
    setSelectedReportIds((prev) =>
      prev.includes(idStr) ? prev.filter((x) => x !== idStr) : [...prev, idStr]
    );
  };

  const selectAllUnbilledInRange = () => {
    const ids = (filteredReports || [])
      .filter((r) => isReportLabourUnbilled(r))
      .map((r) => String(r._id))
      .filter(Boolean);
    setSelectedReportIds(ids);
  };

  const handleUpdateReportStatus = async ({ status, reduction, invoicedNumber, invoicedAt } = {}) => {
    if (!selectedProjectId || !status || selectedReportIds.length === 0) return;
    setMarkReportsLoading(true);
    try {
      await axios.post("/admin/billing/reports/status", {
        reportIds: selectedReportIds,
        projectId: selectedProjectId,
        status,
        ...(reduction ? { reduction } : {}),
        ...(invoicedNumber ? { invoicedNumber } : {}),
        ...(invoicedAt ? { invoicedAt } : {}),
      });
      setShowReducedModal(false);
      setShowLabourInvoicedModal(false);
      setSelectedManualStatus("");
      setReducedMode("percentage");
      setReducedValue("");
      setReducedNote("");
      setLabourInvoicedNumber("");
      setLabourInvoicedDate(dayjs().format("YYYY-MM-DD"));
      setSelectedReportIds([]);
      fetchReports();
      fetchBillingOverview();
      fetchProjectBillingGroups();
    } catch (err) {
      console.error("Update labour status", err);
    } finally {
      setMarkReportsLoading(false);
    }
  };

  const handleStatusSelect = async (nextStatus) => {
    if (!nextStatus || selectedReportIds.length === 0 || !selectedProjectId || markReportsLoading) {
      return;
    }
    if (nextStatus === "invoiced") {
      setShowLabourInvoicedModal(true);
      return;
    }
    if (nextStatus === "reduced") {
      setSelectedManualStatus(nextStatus);
      setShowReducedModal(true);
      return;
    }
    await handleUpdateReportStatus({ status: nextStatus });
  };

  const handleReducedSubmit = async () => {
    const numericValue = Number(reducedValue);
    if (!Number.isFinite(numericValue) || numericValue < 0) return;
    if (reducedMode === "percentage" && numericValue > 100) return;
    await handleUpdateReportStatus({
      status: "reduced",
      reduction: {
        mode: reducedMode,
        value: numericValue,
        ...(reducedNote.trim() ? { note: reducedNote.trim() } : {}),
      },
    });
  };

  const handleLabourInvoicedSubmit = async () => {
    const number = String(labourInvoicedNumber || "").trim();
    if (!number || !labourInvoicedDate) return;
    await handleUpdateReportStatus({
      status: "invoiced",
      invoicedNumber: number,
      invoicedAt: new Date(labourInvoicedDate).toISOString(),
    });
  };

  const toggleBillingGroupExpanded = (key) => {
    setExpandedBillingGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleOverviewRowClick = (row) => {
    const id = row?.projectId != null ? String(row.projectId) : "";
    if (!id) return;
    setSelectedProjectId(id);
    const todayEnd = dayjs().endOf("day").toDate();
    const fromCandidates = [row?.unbilledDateFrom, row?.supplierPendingDateFrom]
      .filter(Boolean)
      .map((d) => dayjs(d))
      .filter((d) => d.isValid());
    if (fromCandidates.length > 0) {
      const earliest = fromCandidates.reduce((min, cur) => (cur.isBefore(min) ? cur : min));
      setDateRange({
        from: earliest.startOf("day").toDate(),
        to: todayEnd,
      });
    } else {
      setDateRange({
        from: dayjs().subtract(29, "day").startOf("day").toDate(),
        to: todayEnd,
      });
    }
    setReportsStatusFilter(UNBILLED_REPORT_STATUSES);
    projectSelectRef.current?.focus?.();
    projectSelectRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  };

  const handleNetSumDoubleClick = (e) => {
    handleCopyToClipboard(Number(netSum).toFixed(2), netSumInputRef.current ?? e?.target);
  };

  const handleExportTimeReportsCSV = async (mode) => {
    if (!selectedProjectId) return;
    const visibleReports = filteredReports || [];
    const reportIds = visibleReports.map((r) => r._id).filter(Boolean);
    if (reportIds.length === 0) {
      alert("No time reports in the table to export.");
      return;
    }
    const details = mode === "tasks" ? "tasks" : undefined;
    const reportDates = visibleReports
      .map((r) => (r.date ? dayjs(r.date) : null))
      .filter((d) => d && d.isValid());
    const fileStart = reportDates.length
      ? reportDates.reduce((min, d) => (d.isBefore(min) ? d : min)).format("YYYY-MM-DD")
      : startDate;
    const fileEnd = reportDates.length
      ? reportDates.reduce((max, d) => (d.isAfter(max) ? d : max)).format("YYYY-MM-DD")
      : endDate;
    try {
      const resp = await axios.post(
        `/exports/reports/project/${selectedProjectId}`,
        { reportIds, ...(details ? { details } : {}) },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const projectName = (projects || []).find((p) => p._id === selectedProjectId)?.name || "project";
      const safeName = projectName.replace(/\s+/g, "_");
      const suffix = mode === "tasks" ? "_tasks" : "";
      const filename = `${safeName}_reports${suffix}_${fileStart}_${fileEnd}.csv`;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      setTimeReportsExportOpen(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        alert("No time reports found in the table.");
      } else {
        console.error("Export failed", err);
        alert("Export failed.");
      }
    }
  };

  const handleDownloadBillingPdfs = async () => {
    const useSelectedInvoices =
      viewMode === "invoices" && selectedInvoiceIds.length > 0;
    const candidateIds = useSelectedInvoices
      ? selectedInvoiceIds
      : viewMode === "invoices"
        ? (invoices || []).map((inv) => inv._id)
        : [...new Set((lineItems || []).map((li) => li.invoiceId).filter(Boolean))];
    const invoiceIds = candidateIds.filter((id) => isInvoiceEligibleForPdfDownload(id, invoices));
    if (!candidateIds.length) {
      alert(
        useSelectedInvoices
          ? "No invoices selected."
          : viewMode === "invoices"
            ? "No invoices in this range."
            : "No invoice line items in this range."
      );
      return;
    }
    if (!invoiceIds.length) {
      alert(
        useSelectedInvoices
          ? "No invoice PDFs to download for the selected invoices."
          : "No invoice PDFs to download — all invoices in this range are marked not billable only."
      );
      return;
    }
    const metaById = {};
    (invoices || []).forEach((inv) => {
      if (inv._id) metaById[String(inv._id)] = { invoiceNumber: inv.invoiceNumber, supplierName: inv.supplierName };
    });
    setDownloadingPdfs(true);
    let downloaded = 0;
    try {
      for (const id of invoiceIds) {
        try {
          const res = await axios.get(`/admin/invoices/${id}/pdf`, { responseType: "blob" });
          const blob = res.data;
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          const meta = metaById[String(id)];
          const fileNameParts = [];
          if (meta?.invoiceNumber) fileNameParts.push(meta.invoiceNumber);
          if (meta?.supplierName) fileNameParts.push(meta.supplierName);
          const baseName = fileNameParts.join(" - ").trim() || `invoice-${String(id)}`;
          link.href = url;
          link.download = `${baseName}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          downloaded += 1;
        } catch (err) {
          if (err.response?.status !== 404) console.error("Download PDF", id, err);
        }
      }
      if (downloaded === 0) alert("No invoice PDFs available to download.");
    } catch (err) {
      console.error("Download PDFs", err);
      alert("Failed to download some invoice PDFs.");
    } finally {
      setDownloadingPdfs(false);
    }
  };

  const toggleWorkerExpanded = (workerId) => {
    setExpandedWorkerIds((prev) => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  const toggleInvoiceExpanded = (invoiceId) => {
    setExpandedInvoiceIds((prev) => {
      const next = new Set(prev);
      const idStr = invoiceId != null ? String(invoiceId) : null;
      if (!idStr) return prev;
      if (next.has(idStr)) next.delete(idStr);
      else next.add(idStr);
      return next;
    });
  };

  const SortableHead = ({ column, children }) => (
    <th
      className="text-left py-2 px-3 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-100"
      onClick={() => handleSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {lineItemsSortBy === column && (
          <span className="text-xs">{lineItemsSortOrder === "asc" ? "↑" : "↓"}</span>
        )}
      </span>
    </th>
  );

  const unbilledInRangeCount = React.useMemo(
    () => (filteredReports || []).filter((r) => isReportLabourUnbilled(r)).length,
    [filteredReports]
  );

  return (
    <PageWrapper>
      <PageHeader title="Billing" />
      <div className="space-y-4">
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Overview</h2>
            <p className="text-sm text-gray-500 mt-1">
              Unbilled labour and supplier documents not yet marked invoiced. Est. labour cost uses each worker’s
              internal hourly cost (same basis as Project details → Economy). Click a row to select that project.
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-x-auto">
            {overviewLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">Loading overview…</div>
            ) : billingOverview.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">No projects to show.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Project</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Unbilled range</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Time reports</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Workers</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Unbilled h</th>
                    <th
                      className="text-right py-2 px-3 font-medium text-gray-700"
                      title="Unbilled hours × worker hourly cost (internal), same as Economy labour cost — not customer billing rates"
                    >
                      Est. labour cost
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Supplier pending</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Pending net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {billingOverview.map((row) => {
                    const needs =
                      (row.unbilledHours > 0) || (row.supplierInvoicesPendingCount > 0);
                    const pid = row.projectId != null ? String(row.projectId) : "";
                    const selected = pid && selectedProjectId === pid;
                    return (
                      <tr
                        key={pid || row.projectName}
                        className={`cursor-pointer hover:bg-blue-50/80 ${needs ? "border-l-4 border-l-amber-400" : "border-l-4 border-l-transparent"} ${selected ? "bg-blue-50" : ""}`}
                        onClick={() => handleOverviewRowClick(row)}
                      >
                        <td className="py-2 px-3 text-gray-900 font-medium">
                          <span className="inline-flex items-center gap-2">
                            <span>{row.projectName ?? "—"}</span>
                            <ProjectStatusSymbol
                              project={{
                                status: row.status,
                                startDate: row.startDate,
                                endDate: row.endDate,
                              }}
                              className="h-4 w-4 text-gray-500"
                            />
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-700 whitespace-nowrap text-sm">
                          {formatOverviewUnbilledRange(row.unbilledDateFrom, row.unbilledDateTo)}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-800">
                          {row.unbilledReportCount ?? 0}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-800">
                          {row.unbilledWorkerCount ?? 0}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {Number(row.unbilledHours || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-700">
                          {row.unbilledLabourEstimate != null && row.unbilledLabourEstimate > 0
                            ? Number(row.unbilledLabourEstimate).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : "—"}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">{row.supplierInvoicesPendingCount ?? 0}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-700">
                          {Number(row.supplierInvoicesPendingNet || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[200px]">
            <label htmlFor="billing-project" className="block text-xs font-medium text-gray-600 mb-1">
              Project
            </label>
            <select
              id="billing-project"
              ref={projectSelectRef}
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={projectsLoading}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select project</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date range</label>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder="Select range"
              presets={PRESETS}
            />
          </div>
        </div>

        {!hasProjectAndRange && (
          <p className="text-sm text-gray-500">Select a project and date range to load time reports and invoice line items.</p>
        )}

        {hasProjectAndRange && (
          <>
            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <h2 className="text-lg font-semibold text-gray-800">Time reports</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative inline-block" ref={reportsStatusDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setReportsStatusDropdownOpen((o) => !o)}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm min-w-[170px] text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <span>
                        {(Array.isArray(reportsStatusFilter) ? reportsStatusFilter.length : 0) ===
                        REPORT_STATUS_OPTIONS.length
                          ? "All report status"
                          : (reportsStatusFilter?.length || 0) === 0
                            ? "All report status"
                            : `${reportsStatusFilter?.length || 0} report status`}
                      </span>
                      <span className="text-gray-400">{reportsStatusDropdownOpen ? "▲" : "▼"}</span>
                    </button>
                    {reportsStatusDropdownOpen && (
                      <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] py-2 px-2 bg-white border border-gray-200 rounded shadow-lg">
                        {REPORT_STATUS_OPTIONS.map((opt) => (
                          <label key={opt.value} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={(reportsStatusFilter || []).includes(opt.value)}
                              onChange={() => {
                                setReportsStatusFilter((f) => {
                                  const cur = f || [];
                                  return cur.includes(opt.value)
                                    ? cur.filter((s) => s !== opt.value)
                                    : [...cur, opt.value];
                                });
                              }}
                              className="rounded border-gray-300"
                            />
                            {opt.label}
                          </label>
                        ))}
                        <div className="border-t mt-1 pt-1 flex gap-2">
                          <button
                            type="button"
                            onClick={() => setReportsStatusFilter(REPORT_STATUS_OPTIONS.map((o) => o.value))}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            All
                          </button>
                          <button
                            type="button"
                            onClick={() => setReportsStatusFilter([])}
                            className="text-xs text-gray-600 hover:text-gray-800"
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={() => setReportsStatusFilter(UNBILLED_REPORT_STATUSES)}
                            className="text-xs text-amber-700 hover:text-amber-900"
                          >
                            Unbilled only
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={selectAllUnbilledInRange}
                    disabled={!hasProjectAndRange || unbilledInRangeCount === 0}
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Select unbilled ({unbilledInRangeCount})
                  </button>
                  <div className="flex items-center gap-2">
                    <label htmlFor="manual-report-status" className="text-sm text-gray-700">
                      Change status to
                    </label>
                    <select
                      id="manual-report-status"
                      value={selectedManualStatus}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedManualStatus(value);
                        if (value) {
                          void handleStatusSelect(value);
                        }
                      }}
                      disabled={!hasProjectAndRange || selectedReportIds.length === 0 || markReportsLoading}
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select ({selectedReportIds.length})</option>
                      {MANUAL_REPORT_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative inline-block" ref={timeReportsExportRef}>
                    <button
                      type="button"
                      onClick={() => setTimeReportsExportOpen((o) => !o)}
                      disabled={!hasProjectAndRange || reportsLoading || filteredReports.length === 0}
                      aria-expanded={timeReportsExportOpen}
                      aria-haspopup="menu"
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                      Export to CSV
                      <span className="text-gray-400" aria-hidden>
                        {timeReportsExportOpen ? "▲" : "▼"}
                      </span>
                    </button>
                    {timeReportsExportOpen && (
                      <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] py-1 bg-white border border-gray-200 rounded-md shadow-lg">
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => handleExportTimeReportsCSV("description")}
                        >
                          Export with Description
                        </button>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => handleExportTimeReportsCSV("tasks")}
                        >
                          Export with Tasks
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto rounded border border-gray-200">
                {reportsLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
                ) : reports.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">No time reports in this range.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-8 py-2 px-2 font-medium text-gray-700" aria-label="Expand" />
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Name</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Hours</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Rate</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700">Total</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-700">Status</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-700">Inv. #</th>
                        <th className="text-left py-2 px-2 font-medium text-gray-700">Inv. date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {groupedByWorker.map((row) => {
                        const isExpanded = expandedWorkerIds.has(row.workerId);
                        const panelId = `worker-reports-${row.workerId}`;
                        const rowTotal = Number(row.totalAmount || 0);
                        return (
                          <React.Fragment key={row.workerId}>
                            <tr className="hover:bg-gray-50/50">
                              <td className="py-2 px-2 align-top">
                                <button
                                  type="button"
                                  onClick={() => toggleWorkerExpanded(row.workerId)}
                                  aria-expanded={isExpanded}
                                  aria-controls={panelId}
                                  className="p-1 rounded text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  title={isExpanded ? "Collapse" : "Expand"}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </td>
                              <td
                                className="py-2 px-3 text-gray-800 font-medium cursor-pointer hover:bg-gray-100 rounded"
                                title="Click to copy"
                                onClick={(e) => handleCopyToClipboard(row.workerName ?? "", e)}
                              >
                                {row.workerName}
                              </td>
                              <td
                                className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:bg-gray-100 rounded"
                                title="Click to copy"
                                onClick={(e) => handleCopyToClipboard(Number(row.totalHours).toFixed(2), e)}
                              >
                                {Number(row.totalHours).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td
                                className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:bg-gray-100 rounded"
                                title={row.rate != null && row.rate !== 0 ? "Click to copy" : undefined}
                                onClick={(e) => {
                                  if (row.rate != null && row.rate !== 0) handleCopyToClipboard(Number(row.rate).toFixed(2), e);
                                }}
                              >
                                {row.rate != null && row.rate !== 0
                                  ? Number(row.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                  : "—"}
                              </td>
                              <td
                                className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:bg-gray-100 rounded"
                                title="Click to copy"
                                onClick={(e) => handleCopyToClipboard(Number(rowTotal).toFixed(2), e)}
                              >
                                {Number(rowTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-2 text-gray-400 text-xs">—</td>
                              <td className="py-2 px-2 text-gray-400 text-xs">—</td>
                              <td className="py-2 px-2 text-gray-400 text-xs">—</td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} className="p-0 align-top">
                                  <div id={panelId} className="bg-gray-50/70 px-3 py-2 border-t border-gray-100" role="region" aria-label={`Reports for ${row.workerName}`}>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                                          <th className="w-8 py-1 pr-1" aria-label="Select" />
                                          <th className="py-1 pr-2">Date</th>
                                          <th className="py-1 pr-2 text-right">Hours</th>
                                          <th className="py-1 pr-2">Status</th>
                                          <th className="py-1 pr-2">Reduction</th>
                                          <th className="py-1 pr-2">Inv. #</th>
                                          <th className="py-1 pr-2">Inv. date</th>
                                          <th className="py-1">Description</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100/80">
                                        {row.reports.map((r) => {
                                          const rid = r._id != null ? String(r._id) : "";
                                          const unbilled = isReportLabourUnbilled(r);
                                          const st = r.status ?? "new";
                                          const writtenDescription = getReportWrittenDescriptionDisplay(r);
                                          const showDescriptionIcon =
                                            Boolean(writtenDescription) && (r.taskEntries?.length ?? 0) > 0;
                                          return (
                                            <tr key={rid} className="align-top">
                                              <td className="py-1.5 pr-1">
                                                {unbilled ? (
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedReportIds.includes(rid)}
                                                    onChange={() => toggleReportSelection(rid)}
                                                    className="rounded border-gray-300"
                                                    aria-label={`Select report ${rid}`}
                                                  />
                                                ) : (
                                                  <span className="inline-block w-4" />
                                                )}
                                              </td>
                                              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-800">
                                                {r.date ? dayjs(r.date).format("YYYY-MM-DD") : "—"}
                                              </td>
                                              <td className="py-1.5 pr-2 text-right tabular-nums">
                                                {r.hours != null ? Number(r.hours) : "—"}
                                              </td>
                                              <td className="py-1.5 pr-2">
                                                <span
                                                  className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${
                                                    st === "invoiced"
                                                      ? "bg-green-100 text-green-800"
                                                      : st === "reduced"
                                                        ? "bg-orange-100 text-orange-800"
                                                        : st === "not_billable"
                                                          ? "bg-amber-100 text-amber-900"
                                                      : "bg-gray-100 text-gray-700"
                                                  }`}
                                                >
                                                  {LABOUR_STATUS_LABELS[st] ?? st}
                                                </span>
                                              </td>
                                              <td className="py-1.5 pr-2 text-gray-700 whitespace-nowrap">
                                                {formatReduction(r)}
                                              </td>
                                              <td className="py-1.5 pr-2 text-gray-700 max-w-[140px] truncate" title={r.invoicedNumber ?? ""}>
                                                {r.invoicedNumber ?? "—"}
                                              </td>
                                              <td className="py-1.5 pr-2 text-gray-700 whitespace-nowrap">
                                                {r.invoicedAt ? dayjs(r.invoicedAt).format("YYYY-MM-DD") : "—"}
                                              </td>
                                              <td className="py-1.5 text-gray-600 min-w-0">
                                                <div className="flex items-start gap-1.5 min-w-0">
                                                  <span className="min-w-0" title={formatReportDescriptionDisplay(r)}>
                                                    {formatReportDescriptionDisplay(r)}
                                                  </span>
                                                  {showDescriptionIcon && (
                                                    <button
                                                      type="button"
                                                      data-report-description-trigger={rid}
                                                      onClick={(e) =>
                                                        toggleReportDescriptionPopover(rid, writtenDescription, e)
                                                      }
                                                      className={`flex-shrink-0 rounded p-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                        reportDescriptionPopover?.reportId === rid
                                                          ? "text-blue-600"
                                                          : "text-gray-400 hover:text-blue-600"
                                                      }`}
                                                      aria-label="View written description"
                                                      aria-expanded={reportDescriptionPopover?.reportId === rid}
                                                    >
                                                      <FaAlignLeft className="w-3.5 h-3.5" aria-hidden />
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <tr className="bg-gray-100 border-t-2 border-gray-200 font-semibold">
                        <td className="py-2 px-2" />
                        <td
                          className="py-2 px-3 text-gray-800 cursor-pointer hover:bg-gray-200 rounded"
                          title="Click to copy"
                          onClick={(e) => handleCopyToClipboard("Sum", e)}
                        >
                          Sum
                        </td>
                        <td
                          className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:bg-gray-200 rounded"
                          title="Click to copy"
                          onClick={(e) => handleCopyToClipboard(Number(groupedByWorker.reduce((s, r) => s + r.totalHours, 0)).toFixed(2), e)}
                        >
                          {Number(groupedByWorker.reduce((s, r) => s + r.totalHours, 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">—</td>
                        <td
                          className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:bg-gray-200 rounded"
                          title="Click to copy"
                          onClick={(e) => handleCopyToClipboard(Number(groupedByWorker.reduce((s, r) => s + Number(r.totalAmount || 0), 0)).toFixed(2), e)}
                        >
                          {Number(groupedByWorker.reduce((s, r) => s + Number(r.totalAmount || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-2 px-2 text-gray-400">—</td>
                        <td className="py-2 px-2 text-gray-400">—</td>
                        <td className="py-2 px-2 text-gray-400">—</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between gap-3 mb-2">
                <h2 className="text-lg font-semibold text-gray-800">Customer invoices (grouped)</h2>
                <p className="text-xs text-gray-500 max-w-xl text-right">
                  Labour and supplier records that share the same invoice number and date appear as one row.
                </p>
              </div>
              <div className="overflow-x-auto rounded border border-gray-200">
                {!selectedProjectId ? (
                  <div className="py-8 text-center text-sm text-gray-500">Select a project to load grouped invoices.</div>
                ) : billingGroupsLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
                ) : billingGroups.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">No invoiced labour or supplier rows to group yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-8 py-2 px-2" aria-label="Expand" />
                        <th className="text-left py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Our inv. #" />
                        </th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Inv. date" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Reports" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Hours" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Hours reduced" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Labour" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Suppliers" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Customer supp. net" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Credits net" className="block text-right" />
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-gray-700 align-bottom">
                          <CustomerInvoiceTableHeading label="Total" className="block text-right" />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {billingGroups.map((g, idx) => {
                        const key = `${g.invoicedNumber ?? ""}|${g.invoicedAt ? new Date(g.invoicedAt).toISOString() : ""}|${idx}`;
                        const expanded = expandedBillingGroupKeys.has(key);
                        return (
                          <React.Fragment key={key}>
                            <tr className="hover:bg-gray-50/50">
                              <td className="py-2 px-2 align-top">
                                <button
                                  type="button"
                                  onClick={() => toggleBillingGroupExpanded(key)}
                                  className="p-1 rounded text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  aria-expanded={expanded}
                                  title={expanded ? "Collapse" : "Expand"}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </td>
                              <td className="py-2 px-3 text-gray-900 font-medium">{g.invoicedNumber ?? "—"}</td>
                              <td className="py-2 px-3 whitespace-nowrap">
                                {g.invoicedAt ? dayjs(g.invoicedAt).format("YYYY-MM-DD") : "—"}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums">{g.reportCount ?? 0}</td>
                              <td className="py-2 px-3 text-right tabular-nums">
                                {Number(g.totalHours ?? 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums text-orange-800">
                                {formatHoursReducedCell(g.hoursReduced)}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums">
                                {Number(g.labourBilled ?? 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums">{g.supplierInvoiceCount ?? 0}</td>
                              <td className="py-2 px-3 text-right tabular-nums">
                                {Number(g.supplierNetSum ?? 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums text-red-700">
                                {formatCreditsNetCell(g.creditsNetSum)}
                              </td>
                              <td className="py-2 px-3 text-right tabular-nums font-medium text-gray-900">
                                {Number(g.totalSum ?? 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                            {expanded && (
                              <tr>
                                <td colSpan={11} className="p-0 bg-slate-50/80">
                                  <div className="px-4 py-3 text-xs text-gray-600 space-y-3">
                                    {(g.reportIds?.length > 0) && (
                                      <div>
                                        <div className="font-semibold text-gray-700 mb-1">Time reports</div>
                                        <p className="font-mono break-all">{g.reportIds.map((id) => String(id)).join(", ")}</p>
                                      </div>
                                    )}
                                    {(g.supplierInvoices?.length > 0) && (
                                      <div>
                                        <div className="font-semibold text-gray-700 mb-1">Supplier invoices</div>
                                        <ul className="space-y-1">
                                          {g.supplierInvoices.map((si) => {
                                            const supplierNet =
                                              si.supplierNet != null ? Number(si.supplierNet) : si.net != null ? Number(si.net) : null;
                                            const customerBillable =
                                              si.customerBillable != null
                                                ? Number(si.customerBillable)
                                                : si.customerNet != null
                                                  ? Number(si.customerNet)
                                                  : si.net != null
                                                    ? Number(si.net)
                                                    : null;
                                            const customerCredits =
                                              si.customerCredits != null ? Number(si.customerCredits) : 0;
                                            const addonPct = Number(si.invoicedAddonPct ?? 0);
                                            return (
                                              <li key={String(si._id)} className="flex flex-wrap gap-x-3 gap-y-0.5">
                                                <span>{si.supplierName ?? "—"}</span>
                                                <span className="text-gray-500">{si.invoiceNumber ?? "—"}</span>
                                                <span className="tabular-nums">
                                                  Supplier net{" "}
                                                  {supplierNet != null
                                                    ? supplierNet.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                      })
                                                    : "—"}
                                                </span>
                                                <span className="tabular-nums font-medium text-gray-800">
                                                  Customer net{" "}
                                                  {customerBillable != null
                                                    ? customerBillable.toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                      })
                                                    : "—"}
                                                  {addonPct > 0 ? ` (+${addonPct}%)` : ""}
                                                </span>
                                                {customerCredits < 0 && (
                                                  <span className="tabular-nums text-red-700">
                                                    Credits {formatCreditsNetCell(customerCredits)}
                                                  </span>
                                                )}
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            <section>
              <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {viewMode === "invoices" ? "Invoices" : "Invoice line items"}
                  </h2>
                  <div className="flex rounded border border-gray-300 bg-gray-100 p-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewMode !== "invoices") {
                          setHighlightedLineItemIds(new Set());
                          setHighlightedInvoiceIds(new Set());
                          setSelectedInvoiceIds([]);
                        }
                        setViewMode("invoices");
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${viewMode === "invoices" ? "bg-white text-gray-800 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      Invoices
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (viewMode !== "line-items") {
                          setHighlightedLineItemIds(new Set());
                          setHighlightedInvoiceIds(new Set());
                          setSelectedInvoiceIds([]);
                        }
                        setViewMode("line-items");
                      }}
                      className={`px-3 py-1.5 text-sm font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${viewMode === "line-items" ? "bg-white text-gray-800 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
                    >
                      Line items
                    </button>
                  </div>
                  <div className="relative inline-block" ref={statusDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setStatusDropdownOpen((o) => !o)}
                        className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm min-w-[140px] text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <span>
                          {(Array.isArray(statusFilter) ? statusFilter.length : 0) === INVOICE_STATUS_OPTIONS.length
                            ? "All Status"
                            : (statusFilter?.length || 0) === 0
                              ? "All Status"
                              : `${statusFilter?.length || 0} Status`}
                        </span>
                        <span className="text-gray-400">{statusDropdownOpen ? "▲" : "▼"}</span>
                      </button>
                      {statusDropdownOpen && (
                        <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] py-2 px-2 bg-white border border-gray-200 rounded shadow-lg">
                          {INVOICE_STATUS_OPTIONS.map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                checked={(statusFilter || []).includes(opt.value)}
                                onChange={() => {
                                  setStatusFilter((f) => {
                                    const cur = f || [];
                                    return cur.includes(opt.value)
                                      ? cur.filter((s) => s !== opt.value)
                                      : [...cur, opt.value];
                                  });
                                }}
                                className="rounded border-gray-300"
                              />
                              {opt.label}
                            </label>
                          ))}
                          <div className="border-t mt-1 pt-1 flex gap-1">
                            <button
                              type="button"
                              onClick={() =>
                                setStatusFilter(INVOICE_STATUS_OPTIONS.map((o) => o.value))
                              }
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              All
                            </button>
                            <button
                              type="button"
                              onClick={() => setStatusFilter([])}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      )}
                  </div>
                  <div className="relative inline-block" ref={customerInvoiceDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setCustomerInvoiceDropdownOpen((open) => !open)}
                      className={`rounded border px-2 py-1.5 text-sm min-w-[140px] max-w-[220px] text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        customerInvoiceFilter.length > 0
                          ? "border-green-600 bg-green-50 text-green-900"
                          : "border-gray-300 bg-white text-gray-800"
                      }`}
                      title="Filter by our customer invoice number, within the selected dates"
                      aria-expanded={customerInvoiceDropdownOpen}
                    >
                      <span className="truncate">{customerInvoiceFilterLabel(customerInvoiceFilter)}</span>
                      <span className="text-gray-400 shrink-0">{customerInvoiceDropdownOpen ? "▲" : "▼"}</span>
                    </button>
                    {customerInvoiceDropdownOpen && (
                      <div className="absolute left-0 top-full mt-1 z-50 w-56 py-2 px-2 bg-white border border-gray-200 rounded shadow-lg">
                        {customerInvoiceOptions.length === 0 ? (
                          <p className="px-1 py-1 text-xs text-gray-500">No customer invoice numbers yet.</p>
                        ) : (
                          <>
                            <input
                              type="search"
                              value={customerInvoiceQuery}
                              onChange={(e) => setCustomerInvoiceQuery(e.target.value)}
                              placeholder="Search number"
                              aria-label="Search customer invoice numbers"
                              className="w-full rounded border border-gray-300 px-2 py-1 text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <div className="max-h-60 overflow-y-auto">
                              {visibleCustomerInvoiceOptions.length === 0 ? (
                                <p className="px-1 py-1 text-xs text-gray-500">No matching numbers.</p>
                              ) : (
                                visibleCustomerInvoiceOptions.map((number) => (
                                  <label key={number} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                                    <input
                                      type="checkbox"
                                      checked={customerInvoiceFilter.includes(number)}
                                      onChange={() =>
                                        setCustomerInvoiceFilter((current) =>
                                          current.includes(number)
                                            ? current.filter((n) => n !== number)
                                            : [...current, number]
                                        )
                                      }
                                      className="rounded border-gray-300"
                                    />
                                    <span className="truncate tabular-nums" title={number}>
                                      {number}
                                    </span>
                                  </label>
                                ))
                              )}
                            </div>
                            <div className="border-t mt-1 pt-1">
                              <button
                                type="button"
                                onClick={() => setCustomerInvoiceFilter([])}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                Clear
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {viewMode === "invoices" && (
                    <>
                      <button
                        type="button"
                        onClick={() => setNeedsTaskFilter((v) => !v)}
                        className={`rounded border px-2 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          needsTaskFilter
                            ? "border-amber-400 bg-amber-50 text-amber-900"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Needs task
                      </button>
                      {assignmentProgress.total > 0 && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {assignmentProgress.assigned} / {assignmentProgress.total} line items assigned
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  {viewMode === "invoices" && selectedInvoiceIds.length > 0 ? (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5 invisible">Mark invoiced</label>
                        <button
                          type="button"
                          onClick={handleMarkInvoicedOpen}
                          disabled={markInvoicedLoading}
                          className="rounded border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {markInvoicedLoading ? "Updating…" : `Mark as Invoiced (${selectedInvoiceIds.length})`}
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5 invisible">Download</label>
                        <button
                          type="button"
                          onClick={handleDownloadBillingPdfs}
                          disabled={downloadingPdfs}
                          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingPdfs
                            ? "Downloading…"
                            : `Download PDFs (${selectedInvoiceIds.length})`}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5 invisible">Download</label>
                        <button
                          type="button"
                          onClick={handleDownloadBillingPdfs}
                          disabled={
                            downloadingPdfs ||
                            (viewMode === "invoices" ? !invoices?.length : !lineItems?.length)
                          }
                          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingPdfs ? "Downloading…" : "Download PDFs"}
                        </button>
                      </div>
                      <div>
                        <label htmlFor="addon-pct" className="block text-xs font-medium text-gray-600 mb-0.5">% Addon</label>
                        <div className="flex items-center gap-1">
                          <input
                            id="addon-pct"
                            type="number"
                            min={0}
                            step={1}
                            value={addonPct}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "" || /^\d+$/.test(v)) setAddonPct(v);
                            }}
                            placeholder="0"
                            className="rounded border border-gray-300 px-2 py-1.5 text-sm w-20 text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setApplyAddonToInvoiceNetColumn((v) => !v)}
                            title="Toggle: show each invoice’s Net as billable net × (1 + % Addon). Click again for base billable net."
                            aria-label="Toggle: show each invoice net including % Addon in the Net column"
                            aria-pressed={applyAddonToInvoiceNetColumn}
                            className={`rounded border p-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              applyAddonToInvoiceNetColumn
                                ? "border-amber-400 bg-amber-50 text-amber-900"
                                : "border-gray-300 bg-white hover:bg-gray-100"
                            }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
                              <path fillRule="evenodd" d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-9.25 10.5a.75.75 0 01-1.272-.71l1.992-7.302H3.75a.75.75 0 01-.548-1.262l9.25-10.5a.75.75 0 011.113-.338z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="net-sum" className="block text-xs font-medium text-gray-600 mb-0.5">Net sum</label>
                        <div className="flex items-center gap-1">
                          <input
                            ref={netSumInputRef}
                            id="net-sum"
                            type="text"
                            readOnly
                            value={Number(netSum).toFixed(2)}
                            onDoubleClick={handleNetSumDoubleClick}
                            title="Double-click to copy"
                            className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-sm w-28 text-right cursor-pointer focus:outline-none text-amber-950"
                          />
                          <button
                            type="button"
                            onClick={handleClearNetSum}
                            title="Clear net sum"
                            className="rounded border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            aria-label="Clear net sum"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {viewMode === "line-items" && (
                <div className="flex flex-wrap items-end gap-4 mb-2 pb-2 border-b border-gray-100">
                  <div>
                    <label htmlFor="filter-task" className="block text-xs font-medium text-gray-600 mb-0.5">Task</label>
                    <select
                      id="filter-task"
                      value={taskFilter}
                      onChange={(e) => setTaskFilter(e.target.value)}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm min-w-[180px] max-w-[280px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All tasks</option>
                      {taskOptions.map((opt) => (
                        <option key={opt.taskOverride} value={opt.taskOverride}>
                          {opt.taskName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="filter-billing" className="block text-xs font-medium text-gray-600 mb-0.5">Billing</label>
                    <select
                      id="filter-billing"
                      value={billingFilter}
                      onChange={(e) => setBillingFilter(e.target.value)}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {BILLING_OPTIONS.map((o) => (
                        <option key={o.value || "all"} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto rounded border border-gray-200">
                {lineItemsLoading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
                ) : viewMode === "line-items" ? (
                  lineItems.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-500">
                      {customerInvoiceFilter.length > 0
                        ? "No line items on the selected customer invoice in this date range."
                        : "No invoice line items in this range."}
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <SortableHead column="invoiceDate">Invoice date</SortableHead>
                          <SortableHead column="invoiceNumber">Invoice number</SortableHead>
                          <SortableHead column="name">Name</SortableHead>
                          <SortableHead column="billing">Billing</SortableHead>
                          <th className="w-10 py-2 px-2 text-center font-medium text-gray-700" aria-label="Copy material label">
                            <CopyIcon className="h-4 w-4 mx-auto text-gray-500" />
                          </th>
                          <SortableHead column="taskName">Task</SortableHead>
                          <th
                            className="text-right py-2 px-3 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-100"
                            onClick={() => handleSort("net")}
                          >
                            <span className="inline-flex items-center gap-1 justify-end">
                              Net
                              {lineItemsSortBy === "net" && (
                                <span className="text-xs">{lineItemsSortOrder === "asc" ? "↑" : "↓"}</span>
                              )}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedLineItems.map((li) => {
                          const rowKey = getLineItemKey(li);
                          const isHighlighted = highlightedLineItemIds.has(rowKey);
                          const liNotBillable = isLineItemNotBillable(li);
                          const liHiddenFromProject = isLineItemHiddenFromProject(li);
                          const liExcluded = isLineItemExcludedFromBillableNet(li);
                          return (
                            <tr
                              key={rowKey}
                              className={`hover:bg-gray-50/50 ${isHighlighted ? "bg-green-100" : ""}`}
                            >
                              <td className="py-2 px-3 text-gray-800 whitespace-nowrap">
                                {li.invoiceDate ? dayjs(li.invoiceDate).format("YYYY-MM-DD") : "—"}
                              </td>
                              <td
                                className="py-2 px-3 text-gray-800 cursor-pointer hover:bg-gray-100 rounded"
                                onDoubleClick={(e) => handleCopyToClipboard(li.invoiceNumber ?? "", e)}
                                title="Double-click to copy"
                              >
                                {li.invoiceNumber ?? "—"}
                              </td>
                              <td
                                className="py-2 px-3 text-gray-700 max-w-xs truncate cursor-pointer hover:bg-gray-100 rounded"
                                title={li.name ? "Double-click to copy" : undefined}
                                onDoubleClick={(e) => handleCopyToClipboard(li.name ?? "", e)}
                              >
                                {li.name ?? "—"}
                              </td>
                              <td className="py-2 px-3 text-gray-700">
                                {liNotBillable ? (
                                  <span className={NOT_BILLABLE_BADGE_CLASS}>Not billable</span>
                                ) : liHiddenFromProject ? (
                                  <span className={HIDE_FROM_PROJECT_BADGE_CLASS}>Hide from project</span>
                                ) : li.billing ? (
                                  billingDecisionLabel(li.billing)
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="py-2 px-2 text-center align-middle">
                                <button
                                  type="button"
                                  className={`inline-flex rounded p-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    liExcluded
                                      ? "text-gray-300 cursor-not-allowed"
                                      : "text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                                  }`}
                                  title={liExcluded ? "Excluded from billing summary" : "Copy material label"}
                                  aria-label="Copy material label"
                                  onClick={
                                    liExcluded
                                      ? undefined
                                      : (e) => handleCopyToClipboard(materialLabelText(li.supplierName, li.invoiceNumber), e)
                                  }
                                  disabled={liExcluded}
                                >
                                  <CopyIcon />
                                </button>
                              </td>
                              <td
                                className="py-2 px-3 text-gray-700 max-w-xs truncate cursor-pointer hover:bg-gray-100 rounded"
                                title={li.taskName ? "Double-click to copy" : undefined}
                                onDoubleClick={(e) => handleCopyToClipboard(li.taskName ?? "", e)}
                              >
                                {li.taskName ?? "—"}
                              </td>
                            <td
                              className={`py-2 px-3 text-right ${
                                liExcluded
                                  ? `text-gray-500 cursor-default ${liHiddenFromProject ? "line-through" : ""}`
                                  : "text-gray-800 cursor-pointer hover:underline"
                              }`}
                              onClick={liExcluded ? undefined : (e) => handleLineItemNetClick(li, e)}
                              title={
                                liNotBillable
                                  ? "Not billable — excluded from invoice billable net and net sum"
                                  : liHiddenFromProject
                                    ? "Hide from project — excluded from invoice billable net and net sum"
                                  : "Click to add to net sum and copy value with addon %"
                              }
                              role={liExcluded ? undefined : "button"}
                              tabIndex={liExcluded ? undefined : 0}
                              onKeyDown={
                                liExcluded
                                  ? undefined
                                  : (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleLineItemNetClick(li, e);
                                      }
                                    }
                              }
                            >
                              {li.net != null ? Number(li.net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )
                ) : displayInvoices.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">
                    {customerInvoiceFilter.length > 0
                      ? "No supplier invoices on the selected customer invoice in this date range."
                      : needsTaskFilter
                        ? "No invoices with unassigned line items in this range."
                        : "No invoices in this range."}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-10 py-2 px-2 text-center font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={
                              displayInvoices.length > 0 &&
                              displayInvoices.every((inv) => selectedInvoiceIds.includes(String(inv._id)))
                            }
                            onChange={toggleAllBillingInvoices}
                            className="rounded border-gray-300"
                            title="Select all"
                            aria-label="Select all invoices"
                          />
                        </th>
                        <th className="w-8 py-2 px-2 font-medium text-gray-700" aria-label="Expand" />
                        <SortableHead column="invoiceDate">Invoice date</SortableHead>
                        <SortableHead column="supplierName">Supplier</SortableHead>
                        <SortableHead column="invoiceNumber">Invoice number</SortableHead>
                        <th className="text-left py-2 px-2 font-medium text-gray-700">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-700">Billing</th>
                        <th className="w-10 py-2 px-2 text-center font-medium text-gray-700" aria-label="Copy material label">
                          <CopyIcon className="h-4 w-4 mx-auto text-gray-500" />
                        </th>
                        <th
                          className="text-right py-2 px-3 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-100"
                          onClick={() => handleSort("net")}
                        >
                          <span className="inline-flex items-center gap-1 justify-end">
                            Net
                            {lineItemsSortBy === "net" && (
                              <span className="text-xs">{lineItemsSortOrder === "asc" ? "↑" : "↓"}</span>
                            )}
                          </span>
                        </th>
                        <th className="text-left py-2 px-2 font-medium text-gray-700">Conf.</th>
                        <th className="w-10 py-2 px-2 text-center font-medium text-gray-700" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {displayInvoices.map((inv) => {
                        const invIdStr = inv._id.toString();
                        const billableNetAmount = getInvoiceEffectiveBillableNet(inv);
                        const displayInvoiceNet = applyAddonToInvoiceNetColumn
                          ? netWithAddonPct(billableNetAmount, addonPct)
                          : billableNetAmount;
                        const isHighlighted = highlightedInvoiceIds.has(invIdStr);
                        const isExpanded = expandedInvoiceIds.has(invIdStr);
                        const panelId = `invoice-line-items-${invIdStr}`;
                        const billingEntry = billingDecisionsByInvoiceId.get(invIdStr);
                        const billingChips = billingEntry
                          ? [
                              ...billingEntry.decisions,
                              ...(billingEntry.hasMissing ? ["Missing"] : []),
                            ]
                          : [];
                        const invLineItems = (lineItems || []).filter(
                          (li) => li.invoiceId != null && String(li.invoiceId) === invIdStr
                        );
                        const unassignedCount = getInvoiceUnassignedCount(inv._id, lineItems);
                        const selectedExpandedIds = selectedLineItemIdsByInvoice[invIdStr] || [];
                        return (
                          <React.Fragment key={invIdStr}>
                            <tr
                              className={`hover:bg-gray-50/50 ${isHighlighted ? "bg-green-100" : ""}`}
                            >
                              <td className="py-2 px-2 align-top text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedInvoiceIds.includes(invIdStr)}
                                  onChange={() => toggleBillingInvoiceSelection(inv._id)}
                                  className="rounded border-gray-300 mt-1"
                                  aria-label={`Select invoice ${inv.invoiceNumber ?? invIdStr}`}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="py-2 px-2 align-top">
                                <button
                                  type="button"
                                  onClick={() => toggleInvoiceExpanded(inv._id)}
                                  aria-expanded={isExpanded}
                                  aria-controls={panelId}
                                  className="p-1 rounded text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  title={isExpanded ? "Collapse" : "Expand"}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </td>
                              <td className="py-2 px-3 text-gray-800 whitespace-nowrap">
                                {inv.invoiceDate ? dayjs(inv.invoiceDate).format("YYYY-MM-DD") : "—"}
                              </td>
                              <td
                                className="py-2 px-3 text-gray-700 max-w-xs truncate cursor-pointer hover:bg-gray-100 rounded"
                                title={inv.supplierName ? "Double-click to copy" : undefined}
                                onDoubleClick={(e) => handleCopyToClipboard(inv.supplierName ?? "", e)}
                              >
                                {inv.supplierName ?? "—"}
                              </td>
                              <td
                                className="py-2 px-3 text-gray-800 cursor-pointer hover:bg-gray-100 rounded"
                                onDoubleClick={(e) => handleCopyToClipboard(inv.invoiceNumber ?? "", e)}
                                title="Double-click to copy"
                              >
                                {inv.invoiceNumber ?? "—"}
                              </td>
                              <td className="py-2 px-2 align-top">
                                <span className="inline-flex items-center gap-1.5 flex-wrap">
                                  <InvoiceStatusBadge status={inv.status} />
                                  {inv.status === "invoiced" && String(inv.invoicedNumber ?? "").trim() ? (
                                    <span
                                      className={`inline-flex max-w-[11rem] items-center gap-1 truncate rounded px-1.5 py-0.5 text-xs font-medium tabular-nums cursor-pointer ${
                                        customerInvoiceFilter.includes(String(inv.invoicedNumber).trim())
                                          ? "bg-green-800 text-white"
                                          : "bg-green-50 text-green-900 ring-1 ring-inset ring-green-700/35"
                                      }`}
                                      title={customerInvoiceChipTitle(inv)}
                                      onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyToClipboard(String(inv.invoicedNumber).trim(), e);
                                      }}
                                    >
                                      <span className="font-normal opacity-70">Our</span>
                                      <span className="truncate">{String(inv.invoicedNumber).trim()}</span>
                                    </span>
                                  ) : null}
                                  {unassignedCount > 0 ? (
                                    <span
                                      className="inline-flex items-center justify-center min-w-[1.25rem] rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                                      title={`${unassignedCount} unassigned line item${unassignedCount !== 1 ? "s" : ""}`}
                                    >
                                      {unassignedCount}
                                    </span>
                                  ) : invLineItems.length > 0 ? (
                                    <span
                                      className="inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800"
                                      title="All line items assigned"
                                    >
                                      ✓
                                    </span>
                                  ) : null}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-700">
                                {billingChips.length === 0 ? (
                                  "—"
                                ) : (
                                  <span className="inline-flex flex-wrap gap-1">
                                    {billingChips.map((chip) =>
                                      chip === "Missing" ? (
                                        <span
                                          key="Missing"
                                          className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
                                        >
                                          Missing
                                        </span>
                                      ) : chip === "not_billable" ? (
                                        <span key={chip} className={NOT_BILLABLE_BADGE_CLASS}>
                                          {billingDecisionLabel(chip)}
                                        </span>
                                      ) : chip === "hide_from_project" ? (
                                        <span key={chip} className={HIDE_FROM_PROJECT_BADGE_CLASS}>
                                          {billingDecisionLabel(chip)}
                                        </span>
                                      ) : (
                                        <span
                                          key={chip}
                                          className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800"
                                        >
                                          {billingDecisionLabel(chip)}
                                        </span>
                                      )
                                    )}
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center align-middle">
                                <button
                                  type="button"
                                  className="inline-flex rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  title="Copy material label"
                                  aria-label="Copy material label"
                                  onClick={(e) => handleCopyToClipboard(materialLabelText(inv.supplierName, inv.invoiceNumber), e)}
                                >
                                  <CopyIcon />
                                </button>
                              </td>
                              <td
                                className="py-2 px-3 text-right text-gray-800 cursor-pointer hover:underline"
                                onClick={(e) => handleInvoiceNetClick(inv, e)}
                                title={
                                  applyAddonToInvoiceNetColumn
                                    ? "Net column shows billable net × (1 + % Addon). Click to add that value to net sum and copy."
                                    : "Billable net (excludes Not billable and Hide from project lines). Click to add to net sum and copy with addon %."
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleInvoiceNetClick(inv, e);
                                  }
                                }}
                              >
                                {displayInvoiceNet.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                              <td className="py-2 px-2 align-top">
                                <InvoiceConfidenceCell invoice={inv} onOpenReview={openInvoiceReview} />
                              </td>
                              <td className="py-2 px-2 align-top text-center" onClick={(e) => e.stopPropagation()}>
                                <div
                                  className="relative inline-block"
                                  ref={openActionsId === inv._id ? actionsDropdownRef : null}
                                >
                                  <button
                                    type="button"
                                    data-invoice-actions-trigger={inv._id}
                                    onClick={(e) => openInvoiceActionsMenu(inv, e)}
                                    className="p-1.5 rounded text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    aria-label="Invoice actions"
                                  >
                                    ⋮
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={11} className="p-0 align-top">
                                  <div
                                    id={panelId}
                                    className="bg-gray-50/70 px-4 py-2 border-t border-gray-100"
                                    role="region"
                                    aria-label={`Line items for ${inv.invoiceNumber || invIdStr}`}
                                  >
                                    <LineItemAssignmentToolbar
                                      selectedCount={selectedExpandedIds.length}
                                      taskBundle={taskBundle}
                                      taskOverride={lineItemTaskOverride}
                                      onTaskOverrideChange={setLineItemTaskOverride}
                                      billingOption={lineItemBillingOption}
                                      onBillingOptionChange={setLineItemBillingOption}
                                      onApply={() =>
                                        handleApplyLineItemTaskAndBilling(inv._id, selectedExpandedIds, {
                                          invoiceLineItems: invLineItems,
                                        })
                                      }
                                      onClearSelection={() => clearExpandedLineItemSelection(invIdStr)}
                                      onNewTask={() => {
                                        setNewTaskModalProjectId(selectedProjectId || "");
                                        setShowCreateTaskModal(true);
                                      }}
                                      applySaving={lineItemApplySaving}
                                      projectId={selectedProjectId}
                                      size="xs"
                                    />
                                    {invLineItems.length === 0 ? (
                                      <p className="text-sm text-gray-500">No line items in this view for this invoice.</p>
                                    ) : (
                                      <table className="w-full text-sm min-w-[400px]">
                                        <thead>
                                          <tr className="border-b border-gray-200">
                                            <th className="text-left py-1.5 px-2 font-medium text-gray-600 w-8">
                                              <input
                                                type="checkbox"
                                                checked={
                                                  invLineItems.length > 0 &&
                                                  selectedExpandedIds.length === invLineItems.length
                                                }
                                                onChange={() => toggleAllExpandedLineItems(invIdStr, invLineItems)}
                                                className="rounded border-gray-300"
                                                title="Select all line items"
                                              />
                                            </th>
                                            <th className="text-left py-1.5 px-2 font-medium text-gray-600">Name</th>
                                            <th className="text-left py-1.5 px-2 font-medium text-gray-600">Billing</th>
                                            <th className="w-8 py-1.5 px-1 text-center font-medium text-gray-600" aria-label="Copy material label">
                                              <CopyIcon className="h-3.5 w-3.5 mx-auto text-gray-500" />
                                            </th>
                                            <th className="text-left py-1.5 px-2 font-medium text-gray-600">Task</th>
                                            <th className="text-right py-1.5 px-2 font-medium text-gray-600">Net</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {invLineItems.map((li) => {
                                            const rowKey = getLineItemKey(li);
                                            const liHighlighted = highlightedLineItemIds.has(rowKey);
                                            const liNb = isLineItemNotBillable(li);
                                            const liHidden = isLineItemHiddenFromProject(li);
                                            const liExcluded = isLineItemExcludedFromBillableNet(li);
                                            const isSelected = selectedExpandedIds.includes(li.lineItemId);
                                            const isUnassigned = !String(li.task ?? "").trim();
                                            return (
                                              <tr
                                                key={rowKey}
                                                className={`${liHighlighted ? "bg-green-100/50" : ""} ${isSelected ? "bg-blue-50" : ""} ${isUnassigned ? "border-l-2 border-l-amber-400" : ""}`}
                                              >
                                                <td className="py-1.5 px-2">
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() =>
                                                      toggleExpandedLineItemSelection(invIdStr, li.lineItemId)
                                                    }
                                                    className="rounded border-gray-300"
                                                  />
                                                </td>
                                                <td className="py-1.5 px-2 text-gray-700 max-w-md truncate">
                                                  {li.name ?? "—"}
                                                </td>
                                                <td className="py-1.5 px-2 text-gray-700">
                                                  {liNb ? (
                                                    <span className={NOT_BILLABLE_BADGE_CLASS}>Not billable</span>
                                                  ) : liHidden ? (
                                                    <span className={HIDE_FROM_PROJECT_BADGE_CLASS}>Hide from project</span>
                                                  ) : li.billing ? (
                                                    billingDecisionLabel(li.billing)
                                                  ) : (
                                                    "—"
                                                  )}
                                                </td>
                                                <td className="py-1.5 px-1 text-center align-middle">
                                                  <button
                                                    type="button"
                                                    className={`inline-flex rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                      liExcluded
                                                        ? "text-gray-300 cursor-not-allowed"
                                                        : "text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                                                    }`}
                                                    title={liExcluded ? "Excluded from billing summary" : "Copy material label"}
                                                    aria-label="Copy material label"
                                                    onClick={
                                                      liExcluded
                                                        ? undefined
                                                        : (e) =>
                                                            handleCopyToClipboard(
                                                              materialLabelText(inv.supplierName, inv.invoiceNumber),
                                                              e
                                                            )
                                                    }
                                                    disabled={liExcluded}
                                                  >
                                                    <CopyIcon className="h-3.5 w-3.5" />
                                                  </button>
                                                </td>
                                                <td className="py-1.5 px-2 text-gray-700 max-w-xs">
                                                  {renderAssignedTaskCell(li)}
                                                </td>
                                                <td
                                                  className={`py-1.5 px-2 text-right ${
                                                    liExcluded
                                                      ? `text-gray-500 cursor-default ${liHidden ? "line-through" : ""}`
                                                      : "text-gray-800 cursor-pointer hover:underline"
                                                  }`}
                                                  onClick={liExcluded ? undefined : (e) => handleLineItemNetClick(li, e)}
                                                  title={
                                                    liNb
                                                      ? "Not billable — excluded from invoice billable net and net sum"
                                                      : liHidden
                                                        ? "Hide from project — excluded from invoice billable net and net sum"
                                                      : "Click to add to net sum and copy value with addon %"
                                                  }
                                                  role={liExcluded ? undefined : "button"}
                                                  tabIndex={liExcluded ? undefined : 0}
                                                  onKeyDown={
                                                    liExcluded
                                                      ? undefined
                                                      : (e) => {
                                                          if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            handleLineItemNetClick(li, e);
                                                          }
                                                        }
                                                  }
                                                >
                                                  {li.net != null
                                                    ? Number(li.net).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                      })
                                                    : "—"}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </div>
      {copyPopover && (
        <div
          className="fixed z-50 pointer-events-none px-2 py-1 rounded bg-white text-black text-xs font-medium shadow-md border border-gray-300"
          style={{
            left: copyPopover.x,
            top: copyPopover.y,
            transform: "translate(-50%, -50%)",
          }}
          role="status"
          aria-live="polite"
        >
          Copied!
        </div>
      )}
      {reportDescriptionPopover &&
        createPortal(
          <div
            ref={reportDescriptionPopoverRef}
            className="fixed z-50 max-w-xs px-3 py-2 bg-white border border-gray-200 rounded-md shadow-lg text-sm text-gray-700 whitespace-pre-wrap break-words"
            style={{
              top: reportDescriptionPopover.top,
              left: reportDescriptionPopover.left,
              width: REPORT_DESCRIPTION_POPOVER_WIDTH,
            }}
            role="dialog"
            aria-label="Written description"
          >
            {reportDescriptionPopover.text}
          </div>,
          document.body
        )}
      {showReducedModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!markReportsLoading) {
              setShowReducedModal(false);
              setSelectedManualStatus("");
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-reduced-labour-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="billing-reduced-labour-title" className="text-lg font-semibold mb-2">
              Set reduced labour
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedReportIds.length} report{selectedReportIds.length !== 1 ? "s" : ""} selected.
            </p>
            <div className="space-y-3">
              <div>
                <label htmlFor="billing-reduced-mode" className="block text-sm font-medium text-gray-700 mb-1">
                  Reduction type
                </label>
                <select
                  id="billing-reduced-mode"
                  value={reducedMode}
                  onChange={(e) => setReducedMode(e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="percentage">Reduce hourly rate by percentage</option>
                  <option value="amount">Reduce total by specified amount</option>
                  <option value="hours">Deduct number of hours</option>
                </select>
              </div>
              <div>
                <label htmlFor="billing-reduced-value" className="block text-sm font-medium text-gray-700 mb-1">
                  {reducedMode === "percentage"
                    ? "Percentage"
                    : reducedMode === "amount"
                      ? "Amount"
                      : "Hours"}
                </label>
                <input
                  id="billing-reduced-value"
                  type="number"
                  min="0"
                  step={reducedMode === "percentage" ? "1" : "0.01"}
                  value={reducedValue}
                  onChange={(e) => setReducedValue(e.target.value)}
                  placeholder={reducedMode === "percentage" ? "e.g. 15" : "e.g. 2.5"}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="billing-reduced-note" className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  id="billing-reduced-note"
                  value={reducedNote}
                  onChange={(e) => setReducedNote(e.target.value)}
                  rows={2}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  if (!markReportsLoading) {
                    setShowReducedModal(false);
                    setSelectedManualStatus("");
                  }
                }}
                disabled={markReportsLoading}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReducedSubmit}
                disabled={
                  markReportsLoading ||
                  reducedValue === "" ||
                  Number(reducedValue) < 0 ||
                  (reducedMode === "percentage" && Number(reducedValue) > 100)
                }
                className="rounded border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markReportsLoading ? "Updating…" : "Save reduced status"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLabourInvoicedModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!markReportsLoading) {
              setShowLabourInvoicedModal(false);
              setSelectedManualStatus("");
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-mark-labour-invoiced-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="billing-mark-labour-invoiced-title" className="text-lg font-semibold mb-2">
              Mark labour as invoiced
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedReportIds.length} report{selectedReportIds.length !== 1 ? "s" : ""} selected.
            </p>
            <div className="space-y-3">
              <div>
                <label htmlFor="billing-labour-invoiced-number" className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice number
                </label>
                <input
                  id="billing-labour-invoiced-number"
                  type="text"
                  value={labourInvoicedNumber}
                  onChange={(e) => setLabourInvoicedNumber(e.target.value)}
                  placeholder="e.g. INV-2026-001"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="billing-labour-invoiced-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of invoice
                </label>
                <input
                  id="billing-labour-invoiced-date"
                  type="date"
                  value={labourInvoicedDate}
                  onChange={(e) => setLabourInvoicedDate(e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  if (!markReportsLoading) {
                    setShowLabourInvoicedModal(false);
                    setSelectedManualStatus("");
                  }
                }}
                disabled={markReportsLoading}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLabourInvoicedSubmit}
                disabled={markReportsLoading || !String(labourInvoicedNumber || "").trim() || !labourInvoicedDate}
                className="rounded border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markReportsLoading ? "Updating…" : "Mark as invoiced"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showMarkInvoicedModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !markInvoicedLoading && setShowMarkInvoicedModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-mark-invoiced-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="billing-mark-invoiced-title" className="text-lg font-semibold mb-2">
              Mark as Invoiced
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedInvoiceIds.length} invoice{selectedInvoiceIds.length !== 1 ? "s" : ""} selected. Enter our invoice number and date for reporting.
            </p>
            <div className="space-y-3">
              <div>
                <label htmlFor="billing-mark-invoiced-number" className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice number
                </label>
                <input
                  id="billing-mark-invoiced-number"
                  type="text"
                  value={markInvoicedNumber}
                  onChange={(e) => setMarkInvoicedNumber(e.target.value)}
                  placeholder="e.g. INV-2024-001"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="billing-mark-invoiced-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of invoice
                </label>
                <input
                  id="billing-mark-invoiced-date"
                  type="date"
                  value={markInvoicedDate}
                  onChange={(e) => setMarkInvoicedDate(e.target.value)}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => !markInvoicedLoading && setShowMarkInvoicedModal(false)}
                disabled={markInvoicedLoading}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMarkInvoicedSubmit}
                disabled={markInvoicedLoading || !markInvoicedNumber.trim() || !markInvoicedDate}
                className="rounded border border-transparent bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markInvoicedLoading ? "Updating…" : "Mark as Invoiced"}
              </button>
            </div>
          </div>
        </div>
      )}
      {openActionsId && actionsDropdownPosition && (() => {
        const inv = displayInvoices.find((i) => i._id === openActionsId);
        if (!inv) return null;
        return createPortal(
          <div
            ref={actionsMenuRef}
            className="fixed w-40 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1"
            style={{ top: actionsDropdownPosition.top, left: actionsDropdownPosition.left }}
          >
            <button
              type="button"
              onClick={() => openInvoiceReview(inv)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Review
            </button>
            {inv.gmailIngestMessageId && (
              <>
                <button
                  type="button"
                  onClick={() => handleViewPdf(inv._id)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  View PDF
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadPdf(inv)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Download PDF
                </button>
              </>
            )}
          </div>,
          document.body
        );
      })()}
      <InvoiceReviewModal
        invoice={reviewInvoice}
        onInvoiceChange={setReviewInvoice}
        onClose={() => {
          setReviewInvoice(null);
          setReviewLineItemIds([]);
          setSaveInvoiceError(null);
        }}
        onSave={handleSaveReviewInvoice}
        saveLoading={saveInvoiceLoading}
        saveError={saveInvoiceError}
        projects={projects}
        taskBundle={taskBundle}
        selectedLineItemIds={reviewLineItemIds}
        onToggleLineItemSelection={(lineItemId) => {
          setReviewLineItemIds((prev) => {
            const set = new Set(prev);
            if (set.has(lineItemId)) set.delete(lineItemId);
            else set.add(lineItemId);
            return [...set];
          });
        }}
        onToggleAllLineItems={(items) => {
          const allIds = (items || []).map((li) => li._id).filter(Boolean);
          const allSelected =
            allIds.length > 0 && allIds.every((id) => reviewLineItemIds.includes(id));
          setReviewLineItemIds(allSelected ? [] : [...allIds]);
        }}
        onClearLineItemSelection={() => setReviewLineItemIds([])}
        onDeselectLineItem={(lineItemId) => {
          setReviewLineItemIds((prev) => prev.filter((id) => id !== lineItemId));
        }}
        lineItemTaskOverride={lineItemTaskOverride}
        onLineItemTaskOverrideChange={setLineItemTaskOverride}
        lineItemBillingOption={lineItemBillingOption}
        onLineItemBillingOptionChange={setLineItemBillingOption}
        onApplyLineItemTaskAndBilling={(invoiceId, selectedIds) =>
          handleApplyLineItemTaskAndBilling(invoiceId, selectedIds, { autoAdvance: false })
        }
        lineItemApplySaving={lineItemApplySaving}
        onNewTask={() => {
          setNewTaskModalProjectId(
            normalizeProjectId(reviewInvoice?.projectId) || selectedProjectId || ""
          );
          setShowCreateTaskModal(true);
        }}
        onViewPdf={handleViewPdf}
        onDelete={handleDeleteReviewInvoice}
        deleteLoading={deleteInvoiceLoading}
        showGmailActions={false}
        showReprocess={false}
      />
      {showCreateTaskModal && (
        <TaskBuilderModal
          bundleId={taskBundle?._id}
          projectId={
            newTaskModalProjectId ||
            selectedProjectId ||
            normalizeProjectId(reviewInvoice?.projectId) ||
            ""
          }
          isOpen={showCreateTaskModal}
          onClose={() => {
            setShowCreateTaskModal(false);
            setNewTaskModalProjectId("");
          }}
          onSuccess={handleCreateTaskSuccess}
          projects={projects}
          initialProjectId={
            newTaskModalProjectId ||
            selectedProjectId ||
            normalizeProjectId(reviewInvoice?.projectId) ||
            ""
          }
          initialTaskType="task"
        />
      )}
    </PageWrapper>
  );
}
